import type { MatchingPair } from '../game/rules.ts';

export interface LocalizedQuestionContent {
  questionText: string;
  options: string[];
  correctAnswer: string;
  explanation: string;
  acceptedAnswers?: string[];
  matchingPairs?: MatchingPair[];
}

export interface SeedQuestionItem {
  questionCode: string;
  category: string;
  bibleBook: string;
  testament: 'Old Testament' | 'New Testament';
  difficulty: 'Beginner' | 'Easy' | 'Intermediate' | 'Advanced' | 'Expert';
  questionFormat: string;
  scriptureReference: string;
  en: LocalizedQuestionContent;
  tl: LocalizedQuestionContent;
  ceb: LocalizedQuestionContent;
  ko: LocalizedQuestionContent;
  'zh-CN': LocalizedQuestionContent;
  'zh-TW': LocalizedQuestionContent;
  es?: LocalizedQuestionContent;
}

export const BIBLE_CATEGORIES = [
  { group: 'All', items: ['ALL CATEGORIES'] },
  {
    group: 'Old Testament',
    items: [
      'Genesis',
      'Exodus',
      'Leviticus',
      'Numbers',
      'Deuteronomy',
      'Historical Books',
      'Wisdom Books',
      'Psalms and Proverbs',
      'Major Prophets',
      'Minor Prophets',
    ],
  },
  {
    group: 'New Testament',
    items: [
      'The Four Gospels',
      'Acts',
      'Pauline Epistles',
      'General Epistles',
      'Revelation',
    ],
  },
  {
    group: 'Thematic Categories',
    items: [
      'Biblical Characters',
      'Kings and Queens',
      'Prophets',
      'Apostles',
      'Miracles',
      'Parables',
      'Women of the Bible',
      'Men of the Bible',
      'Biblical Geography',
      'Biblical History',
      'Important Events',
      'Family Relationships',
      'Promises of God',
      'Faith and Obedience',
      'Prayer',
      'Salvation',
      'Wisdom',
      'Church History in the Bible',
      'Teachings of Jesus',
      'Life and Ministry of the Apostles',
    ],
  },
];

export const PRIMARY_QUIZ_TYPES = [
  'Automatic / Mixed',
  'Multiple Choice',
  'True or False',
  'Fill in the Blank',
  'Matching',
  'Short Answer',
  'Mixed Challenge',
  'Open-Ended Reflection',
];

export const QUESTION_FORMATS = [
  'Automatic / Mixed',
  'Multiple Choice',
  'True or False',
  'Fill in the Blank',
  'Matching',
  'Short Answer',
  'Mixed Challenge',
  'Open-Ended Reflection',
  'Complete the Bible Verse',
  'Who Am I?',
  'Biblical Chronology',
  'Guess the Bible Book',
  'Who Said It?',
  'Where Did It Happen?',
  'Bible Riddles',
  'Scripture Reference Challenge',
];

export const DIFFICULTY_LEVELS = [
  'Mixed Difficulty',
  'Beginner',
  'Easy',
  'Intermediate',
  'Advanced',
  'Expert',
];

export const VERIFIED_SEED_QUESTIONS: SeedQuestionItem[] = [
  // 1. Multiple Choice (4 options)
  {
    questionCode: 'QB-GEN-001',
    category: 'Genesis',
    bibleBook: 'Genesis',
    testament: 'Old Testament',
    difficulty: 'Beginner',
    questionFormat: 'Multiple Choice',
    scriptureReference: 'Genesis 6:14',
    en: {
      questionText:
        'From what specific type of wood did God command Noah to build the ark?',
      options: ['Gopher wood', 'Cedar of Lebanon', 'Acacia wood', 'Olive wood'],
      correctAnswer: 'Gopher wood',
      explanation:
        'In Genesis 6:14, God instructed Noah: "Make thee an ark of gopher wood; rooms shalt thou make in the ark, and shalt pitch it within and without with pitch."',
    },
    tl: {
      questionText:
        'Anong partikular na uri ng kahoy ang iniutos ng Diyos kay Noe upang ipanggawa ng daong?',
      options: ['Kahoy na gofer', 'Sedro ng Libano', 'Kahoy na akasya', 'Kahoy na olibo'],
      correctAnswer: 'Kahoy na gofer',
      explanation:
        'Sa Genesis 6:14, iniutos ng Diyos kay Noe na gumawa ng isang daong na yari sa kahoy na gofer at pahiran ito ng alkitran sa loob at labas.',
    },
    ceb: {
      questionText:
        'Unsa nga matang sa kahoy ang gisugo sa Diyos kang Noe sa pagbuhat sa arka?',
      options: ['Kahoy nga gofer', 'Sedro sa Libano', 'Kahoy nga akasya', 'Kahoy nga olibo'],
      correctAnswer: 'Kahoy nga gofer',
      explanation:
        'Sa Genesis 6:14, gisugo sa Diyos si Noe sa pagbuhat og arka nga gama sa kahoy nga gofer ug bulitan kini og alkitran sa sulod ug sa gawas.',
    },
    ko: {
      questionText:
        '하나님께서 노아에게 방주를 만들라고 명하신 나무의 종류는 무엇입니까?',
      options: ['고페르 나무 (잣나무)', '레바논의 백향목', '조각목 (아카시아)', '감람나무'],
      correctAnswer: '고페르 나무 (잣나무)',
      explanation:
        '창세기 6:14에서 하나님께서는 노아에게 "너는 고페르 나무로 너를 위하여 방주를 만들되 그 안에 칸들을 막고 역청을 그 안팎에 칠하라"고 명하셨습니다.',
    },
    'zh-CN': {
      questionText: '上帝吩咐挪亚用哪种特定的树木建造方舟？',
      options: ['歌斐木', '黎巴嫩香柏木', '皂荚木', '橄榄木'],
      correctAnswer: '歌斐木',
      explanation:
        '在创世记 6:14 中，上帝吩咐挪亚：“你要用歌斐木造一只方舟，分一间一间地造，里外抹上松香。”',
    },
    'zh-TW': {
      questionText: '上帝吩咐挪亞用哪種特定的樹木建造方舟？',
      options: ['歌斐木', '黎巴嫩香柏木', '皂莢木', '橄欖木'],
      correctAnswer: '歌斐木',
      explanation:
        '在創世記 6:14 中，上帝吩咐挪亞：「你要用歌斐木造一隻方舟，分一間一間地造，裡外抹上松香。」',
    },
    es: {
      questionText:
        '¿De qué tipo específico de madera mandó Dios a Noé construir el arca?',
      options: ['Madera de gofer', 'Cedro del Líbano', 'Madera de acacia', 'Madera de olivo'],
      correctAnswer: 'Madera de gofer',
      explanation:
        'En Génesis 6:14, Dios instruyó a Noé: "Hazte un arca de madera de gofer; harás aposentos en el arca, y la calafatearás con brea por dentro y por fuera."',
    },
  },

  // 2. Multiple Choice (5 options - varying option count!)
  {
    questionCode: 'QB-EXO-002',
    category: 'Exodus',
    bibleBook: 'Exodus',
    testament: 'Old Testament',
    difficulty: 'Easy',
    questionFormat: 'Multiple Choice',
    scriptureReference: 'Exodus 3:1-2',
    en: {
      questionText:
        'On which mountain did Moses behold the bush burning with fire yet not consumed?',
      options: [
        'Mount Horeb',
        'Mount Carmel',
        'Mount Nebo',
        'Mount Tabor',
        'Mount Gilboa',
      ],
      correctAnswer: 'Mount Horeb',
      explanation:
        'Exodus 3:1-2 records that Moses led the flock of Jethro to Horeb, the mountain of God, where the Angel of the Lord appeared in a flame of fire out of the midst of a bush.',
    },
    tl: {
      questionText:
        'Saang bundok nakita ni Moises ang mababang punongkahoy na nagniningas sa apoy ngunit hindi natutupok?',
      options: [
        'Bundok Horeb',
        'Bundok Carmelo',
        'Bundok Nebo',
        'Bundok Tabor',
        'Bundok Gilboa',
      ],
      correctAnswer: 'Bundok Horeb',
      explanation:
        'Ayon sa Exodo 3:1-2, dinala ni Moises ang kawan sa Horeb, ang bundok ng Diyos, kung saan nagpakita ang anghel ng Panginoon sa nagniningas na mababang punongkahoy.',
    },
    ceb: {
      questionText:
        'Sa unsang bukid nakita ni Moises ang sampinit nga nagdilaab sa kalayo apan wala masunog?',
      options: [
        'Bukid sa Horeb',
        'Bukid sa Carmelo',
        'Bukid sa Nebo',
        'Bukid sa Tabor',
        'Bukid sa Gilboa',
      ],
      correctAnswer: 'Bukid sa Horeb',
      explanation:
        'Sumala sa Exodo 3:1-2, gidala ni Moises ang panon ngadto sa Horeb, ang bukid sa Diyos, diin ang manulonda sa Ginoo mipakita sa nagdilaab nga sampinit.',
    },
    ko: {
      questionText:
        '모세가 불이 붙었으나 타서 사라지지 않는 떨기나무를 본 산은 어디입니까?',
      options: ['호렙산', '갈멜산', '느보산', '다볼산', '길보아산'],
      correctAnswer: '호렙산',
      explanation:
        '출애굽기 3:1-2은 모세가 이드로의 양 떼를 인도하여 하나님의 산 호렙에 이르렀을 때 여호와의 사자가 떨기나무 가운데 불꽃으로 나타나셨음을 기록합니다.',
    },
    'zh-CN': {
      questionText: '摩西在哪座山上看见荆棘被火烧着却没有烧毁？',
      options: ['何烈山', '迦密山', '尼波山', '他泊山', '基利波山'],
      correctAnswer: '何烈山',
      explanation:
        '出埃及记 3:1-2 记载摩西领羊群来到神的山，就是何烈山，耶和华的使者从荆棘里火焰中向他显现。',
    },
    'zh-TW': {
      questionText: '摩西在哪座山上看見荊棘被火燒著卻沒有燒毀？',
      options: ['何烈山', '迦密山', '尼波山', '他泊山', '基利波山'],
      correctAnswer: '何烈山',
      explanation:
        '出埃及記 3:1-2 記載摩西領羊群來到神的山，就是何烈山，耶和華的使者從荊棘裡火焰中向他顯現。',
    },
    es: {
      questionText:
        '¿En qué monte contempló Moisés la zarza que ardía en fuego y no se consumía?',
      options: [
        'Monte Horeb',
        'Monte Carmelo',
        'Monte Nebo',
        'Monte Tabor',
        'Monte Gilboa',
      ],
      correctAnswer: 'Monte Horeb',
      explanation:
        'Éxodo 3:1-2 registra que Moisés llevó las ovejas de Jetro a Horeb, el monte de Dios, donde el Ángel de Jehová apareció en una llama de fuego en medio de una zarza.',
    },
  },

  // 3. Multiple Choice (3 options - varying option count!)
  {
    questionCode: 'QB-WHO-003',
    category: 'Women of the Bible',
    bibleBook: 'Judges',
    testament: 'Old Testament',
    difficulty: 'Intermediate',
    questionFormat: 'Multiple Choice',
    scriptureReference: 'Judges 4:4-5',
    en: {
      questionText:
        'Which prophetess, the wife of Lapidoth, judged Israel while sitting under a palm tree between Ramah and Bethel?',
      options: ['Deborah', 'Hannah', 'Miriam'],
      correctAnswer: 'Deborah',
      explanation:
        'Judges 4:4-5 identifies Deborah, a prophetess and wife of Lapidoth, who judged Israel under the palm tree of Deborah in Mount Ephraim.',
    },
    tl: {
      questionText:
        'Sinong propetisa, na asawa ni Lapidoth, ang humukom sa Israel habang nakaupo sa ilalim ng puno ng palma sa pagitan ng Rama at Bethel?',
      options: ['Debora', 'Ana', 'Miriam'],
      correctAnswer: 'Debora',
      explanation:
        'Sa Hukom 4:4-5, si Debora ay isang propetisa na humukom sa Israel sa ilalim ng puno ng palma sa pagitan ng Rama at Bethel.',
    },
    ceb: {
      questionText:
        'Kinsa nga manalagna nga babaye, asawa ni Lapidoth, ang naghukom sa Israel ilalom sa kahoy nga palma taliwala sa Rama ug Bethel?',
      options: ['Debora', 'Ana', 'Miriam'],
      correctAnswer: 'Debora',
      explanation:
        'Ang Maghuhukom 4:4-5 nag-ila kang Debora isip manalagna ug maghuhukom sa Israel nga naglingkod ilalom sa palma sa Debora.',
    },
    ko: {
      questionText:
        '랍비돗의 아내로서 라마와 벧엘 사이 종려나무 아래에 앉아 이스라엘을 다스린 여선지자는 누구입니까?',
      options: ['드보라', '한나', '미리암'],
      correctAnswer: '드보라',
      explanation:
        '사사기 4:4-5은 에브라임 산지 라마와 벧엘 사이 드보라의 종려나무 아래에 거주하며 이스라엘의 사사로 일한 여선지자 드보라를 기록합니다.',
    },
    'zh-CN': {
      questionText:
        '哪位女先知是拉比多的妻子，曾坐在拉玛和伯特利之间的棕树下审判以色列人？',
      options: ['底波拉', '哈拿', '米利暗'],
      correctAnswer: '底波拉',
      explanation:
        '士师记 4:4-5 记载女先知底波拉是拉比多的妻子，当时作以色列的士师，坐在以法莲山地拉玛和伯特利之间的棕树下。',
    },
    'zh-TW': {
      questionText:
        '哪位女先知是拉比多的妻子，曾坐在拉瑪和伯特利之間的棕樹下審判以色列人？',
      options: ['底波拉', '哈拿', '米利暗'],
      correctAnswer: '底波拉',
      explanation:
        '士師記 4:4-5 記載女先知底波拉是拉比多的妻子，當時作以色列的士師，坐在以法蓮山地拉瑪和伯特利之間的棕樹下。',
    },
    es: {
      questionText:
        '¿Qué profetisa, esposa de Lapidot, juzgaba a Israel sentada debajo de una palmera entre Ramá y Bet-el?',
      options: ['Débora', 'Ana', 'María'],
      correctAnswer: 'Débora',
      explanation:
        'Jueces 4:4-5 identifica a Débora, profetisa y mujer de Lapidot, quien juzgaba a Israel debajo de la palmera de Débora en el monte de Efraín.',
    },
  },

  // 4. Fill in the Blank
  {
    questionCode: 'QB-PSA-004',
    category: 'Psalms and Proverbs',
    bibleBook: 'Psalms',
    testament: 'Old Testament',
    difficulty: 'Beginner',
    questionFormat: 'Fill in the Blank',
    scriptureReference: 'Psalm 119:105',
    en: {
      questionText:
        '"Thy word is a lamp unto my feet, and a ______ unto my path."',
      options: ['light', 'shield', 'guide', 'crown'],
      correctAnswer: 'light',
      acceptedAnswers: ['light', 'a light'],
      explanation:
        'Psalm 119:105 (KJV) declares: "Thy word is a lamp unto my feet, and a light unto my path."',
    },
    tl: {
      questionText:
        '"Ang salita mo\'y ilawan sa aking mga paa, at ______ sa aking landas."',
      options: ['liwanag', 'kalasag', 'gabay', 'korona'],
      correctAnswer: 'liwanag',
      acceptedAnswers: ['liwanag', 'ilaw', 'light'],
      explanation:
        'Ayon sa Awit 119:105: "Ang salita mo\'y ilawan sa aking mga paa, at liwanag sa aking landas."',
    },
    ceb: {
      questionText:
        '"Ang imong pulong maoy suga sa akong mga tiil, ug ______ sa akong alagianan."',
      options: ['kahayag', 'taming', 'giya', 'purongpurong'],
      correctAnswer: 'kahayag',
      acceptedAnswers: ['kahayag', 'hayag', 'light'],
      explanation:
        'Salmo 119:105 nag-ingon: "Ang imong pulong maoy suga sa akong mga tiil, ug kahayag sa akong alagianan."',
    },
    ko: {
      questionText:
        '"주의 말씀은 내 발에 등이요 내 길에 ______이니이다." (시편 119:105)',
      options: ['빛', '방패', '면류관', '지팡이'],
      correctAnswer: '빛',
      acceptedAnswers: ['빛', 'light'],
      explanation:
        '시편 119:105 말씀은 "주의 말씀은 내 발에 등이요 내 길에 빛이니이다"라고 선포합니다.',
    },
    'zh-CN': {
      questionText: '“你的话是我脚前的灯，是我路上的______。”（诗篇 119:105）',
      options: ['光', '盾牌', '冠冕', '磐石'],
      correctAnswer: '光',
      acceptedAnswers: ['光', '亮光', 'light'],
      explanation:
        '诗篇 119:105 宣告：“你的话是我脚前的灯，是我路上的光。”',
    },
    'zh-TW': {
      questionText: '「你的話是我腳前的燈，是我路上的______。」（詩篇 119:105）',
      options: ['光', '盾牌', '冠冕', '磐石'],
      correctAnswer: '光',
      acceptedAnswers: ['光', '亮光', 'light'],
      explanation:
        '詩篇 119:105 宣告：「你的話是我腳前的燈，是我路上的光。」',
    },
    es: {
      questionText:
        '"Lámpara es a mis pies tu palabra, y ______ a mi camino." (Salmos 119:105)',
      options: ['lumbrera', 'escudo', 'guía', 'corona'],
      correctAnswer: 'lumbrera',
      acceptedAnswers: ['lumbrera', 'luz', 'light'],
      explanation:
        'El Salmo 119:105 declara: "Lámpara es a mis pies tu palabra, y lumbrera a mi camino."',
    },
  },

  // 5. Fill in the Blank (Genesis 1:1)
  {
    questionCode: 'QB-GEN-013',
    category: 'Genesis',
    bibleBook: 'Genesis',
    testament: 'Old Testament',
    difficulty: 'Beginner',
    questionFormat: 'Fill in the Blank',
    scriptureReference: 'Genesis 1:1',
    en: {
      questionText:
        '"In the beginning God created the ______ and the earth."',
      options: ['heaven', 'oceans', 'mountains', 'stars'],
      correctAnswer: 'heaven',
      acceptedAnswers: ['heaven', 'heavens', 'the heaven', 'the heavens'],
      explanation:
        'Genesis 1:1 opens Scripture with the words: "In the beginning God created the heaven and the earth."',
    },
    tl: {
      questionText:
        '"Nang pasimula ay nilikha ng Diyos ang ______ at ang lupa." (Genesis 1:1)',
      options: ['langit', 'karagatan', 'kabundukan', 'bituin'],
      correctAnswer: 'langit',
      acceptedAnswers: ['langit', 'kalangitan', 'heaven', 'heavens'],
      explanation:
        'Ang Genesis 1:1 ay nagsisimula sa: "Nang pasimula ay nilikha ng Diyos ang langit at ang lupa."',
    },
    ceb: {
      questionText:
        '"Sa sinugdan gibuhat sa Diyos ang ______ ug ang yuta." (Genesis 1:1)',
      options: ['langit', 'kadagatan', 'kabukiran', 'kabitoonan'],
      correctAnswer: 'langit',
      acceptedAnswers: ['langit', 'kalangitan', 'heaven', 'heavens'],
      explanation:
        'Ang Genesis 1:1 nag-ingon: "Sa sinugdan gibuhat sa Diyos ang langit ug ang yuta."',
    },
    ko: {
      questionText:
        '"태초에 하나님이 ______과(와) 땅을 창조하시니라." (창세기 1:1)',
      options: ['천지 (하늘)', '바다', '산맥', '별들'],
      correctAnswer: '천지 (하늘)',
      acceptedAnswers: ['천지 (하늘)', '하늘', '천지', 'heaven', 'heavens'],
      explanation:
        '창세기 1:1은 "태초에 하나님이 천지를 창조하시니라"로 성경의 첫 장을 엽니다.',
    },
    'zh-CN': {
      questionText: '“起初，神创造______。”（创世记 1:1）',
      options: ['天地', '海洋', '众星', '山岭'],
      correctAnswer: '天地',
      acceptedAnswers: ['天地', '天', '诸天', 'heaven'],
      explanation: '创世记 1:1 记载：“起初，神创造天地。”',
    },
    'zh-TW': {
      questionText: '「起初，神創造______。」（創世記 1:1）',
      options: ['天地', '海洋', '眾星', '山嶺'],
      correctAnswer: '天地',
      acceptedAnswers: ['天地', '天', '諸天', 'heaven'],
      explanation: '創世記 1:1 記載：「起初，神創造天地。」',
    },
    es: {
      questionText:
        '"En el principio creó Dios los ______ y la tierra." (Génesis 1:1)',
      options: ['cielos', 'mares', 'montes', 'astros'],
      correctAnswer: 'cielos',
      acceptedAnswers: ['cielos', 'cielo', 'heaven'],
      explanation:
        'Génesis 1:1 declara: "En el principio creó Dios los cielos y la tierra."',
    },
  },

  // 6. True or False (True statement)
  {
    questionCode: 'QB-TF-014',
    category: 'The Four Gospels',
    bibleBook: 'Matthew',
    testament: 'New Testament',
    difficulty: 'Beginner',
    questionFormat: 'True or False',
    scriptureReference: 'Matthew 2:1',
    en: {
      questionText:
        'According to Matthew 2:1, Jesus was born in Bethlehem of Judaea in the days of Herod the king.',
      options: ['TRUE', 'FALSE'],
      correctAnswer: 'TRUE',
      explanation:
        'Matthew 2:1 explicitly states: "Now when Jesus was born in Bethlehem of Judaea in the days of Herod the king, behold, there came wise men from the east to Jerusalem."',
    },
    tl: {
      questionText:
        'Ayon sa Mateo 2:1, si Hesus ay ipinanganak sa Betlehem ng Judea noong mga araw ni Haring Herodes.',
      options: ['TAMA (TRUE)', 'MALI (FALSE)'],
      correctAnswer: 'TAMA (TRUE)',
      explanation:
        'Nakasaad sa Mateo 2:1 na si Hesus ay ipinanganak sa Betlehem ng Judea sa panahon ni Haring Herodes.',
    },
    ceb: {
      questionText:
        'Sumala sa Mateo 2:1, si Hesus natawo sa Betlehem sa Judea sa mga adlaw ni Hari Herodes.',
      options: ['TINUOD (TRUE)', 'BAKAK (FALSE)'],
      correctAnswer: 'TINUOD (TRUE)',
      explanation:
        'Ang Mateo 2:1 nagpamatuod nga si Hesus natawo sa Betlehem sa Judea sa mga adlaw ni Hari Herodes.',
    },
    ko: {
      questionText:
        '마태복음 2:1에 따르면, 예수님께서는 헤롯 왕 때에 유대 베들레헴에서 나셨습니다.',
      options: ['참 (TRUE)', '거짓 (FALSE)'],
      correctAnswer: '참 (TRUE)',
      explanation:
        '마태복음 2:1은 "헤롯 왕 때에 예수께서 유대 베들레헴에서 나시매 동방으로부터 박사들이 예루살렘에 이르러"라고 기록합니다.',
    },
    'zh-CN': {
      questionText: '根据马太福音 2:1 记载，当希律王的时候，耶稣生在犹太的伯利恒。',
      options: ['正确 (TRUE)', '错误 (FALSE)'],
      correctAnswer: '正确 (TRUE)',
      explanation:
        '马太福音 2:1 明确记载：“当希律王的时候，耶稣生在犹太的伯利恒。有几个博士从东方来到耶路撒冷。”',
    },
    'zh-TW': {
      questionText: '根據馬太福音 2:1 記載，當希律王的時候，耶穌生在猶太的伯利恆。',
      options: ['正確 (TRUE)', '錯誤 (FALSE)'],
      correctAnswer: '正確 (TRUE)',
      explanation:
        '馬太福音 2:1 明確記載：「當希律王的時候，耶穌生在猶太的伯利恆。有幾個博士從東方來到耶路撒冷。」',
    },
    es: {
      questionText:
        'Según Mateo 2:1, Jesús nació en Belén de Judea en días del rey Herodes.',
      options: ['VERDADERO', 'FALSO'],
      correctAnswer: 'VERDADERO',
      explanation:
        'Mateo 2:1 declara: "Cuando Jesús nació en Belén de Judea en días del rey Herodes, vinieron del oriente a Jerusalén unos magos."',
    },
  },

  // 7. True or False (False statement)
  {
    questionCode: 'QB-TF-015',
    category: 'Prophets',
    bibleBook: 'Jonah',
    testament: 'Old Testament',
    difficulty: 'Easy',
    questionFormat: 'True or False',
    scriptureReference: 'Jonah 1:2-3',
    en: {
      questionText:
        'When the Lord first commanded Jonah to go to Nineveh, Jonah immediately obeyed and sailed straight toward Nineveh.',
      options: ['TRUE', 'FALSE'],
      correctAnswer: 'FALSE',
      explanation:
        'False. According to Jonah 1:2-3, when God commanded Jonah to go to Nineveh, Jonah rose up to flee unto Tarshish from the presence of the Lord.',
    },
    tl: {
      questionText:
        'Nang unang utusan ng Panginoon si Jonas na pumunta sa Ninive, agad siyang sumunod at naglayag patungo sa Ninive.',
      options: ['TAMA (TRUE)', 'MALI (FALSE)'],
      correctAnswer: 'MALI (FALSE)',
      explanation:
        'Mali. Ayon sa Jonas 1:2-3, bumangon si Jonas upang tumakas patungo sa Tarsis mula sa harapan ng Panginoon.',
    },
    ceb: {
      questionText:
        'Sa unang pagsugo sa Ginoo kang Jonas sa pag-adto sa Ninive, mituman dayon siya ug milawig padulong sa Ninive.',
      options: ['TINUOD (TRUE)', 'BAKAK (FALSE)'],
      correctAnswer: 'BAKAK (FALSE)',
      explanation:
        'Bakak. Sumala sa Jonas 1:2-3, si Jonas mibangon aron mokagiw paingon sa Tarsis gikan sa presensya sa Ginoo.',
    },
    ko: {
      questionText:
        '여호와께서 요나에게 처음 니느웨로 가라고 명하셨을 때, 요나는 즉시 순종하여 니느웨로 직행했습니다.',
      options: ['참 (TRUE)', '거짓 (FALSE)'],
      correctAnswer: '거짓 (FALSE)',
      explanation:
        '거짓입니다. 요나 1:2-3에 따르면 요나는 여호와의 얼굴을 피하여 다시스로 도망하려 배를 탔습니다.',
    },
    'zh-CN': {
      questionText: '当耶和华初次吩咐约拿往尼尼微去时，约拿立刻顺服并直接乘船前往尼尼微。',
      options: ['正确 (TRUE)', '错误 (FALSE)'],
      correctAnswer: '错误 (FALSE)',
      explanation:
        '错误。根据约拿书 1:2-3 记载，约拿却起来逃往他施去躲避耶和华。',
    },
    'zh-TW': {
      questionText: '當耶和華初次吩咐約拿往尼尼微去時，約拿立刻順服並直接乘船前往尼尼微。',
      options: ['正確 (TRUE)', '錯誤 (FALSE)'],
      correctAnswer: '錯誤 (FALSE)',
      explanation:
        '錯誤。根據約拿書 1:2-3 記載，約拿卻起來逃往他施去躲避耶和華。',
    },
    es: {
      questionText:
        'Cuando Jehová mandó por primera vez a Jonás ir a Nínive, Jonás obedeció de inmediato y navegó directo hacia Nínive.',
      options: ['VERDADERO', 'FALSO'],
      correctAnswer: 'FALSO',
      explanation:
        'Falso. Según Jonás 1:2-3, Jonás se levantó para huir de la presencia de Jehová a Tarsis.',
    },
  },

  // 8. Short Answer (Biblical Person)
  {
    questionCode: 'QB-SA-016',
    category: 'Biblical Characters',
    bibleBook: '1 Samuel',
    testament: 'Old Testament',
    difficulty: 'Beginner',
    questionFormat: 'Short Answer',
    scriptureReference: '1 Samuel 17:49-50',
    en: {
      questionText:
        'Which young shepherd of Bethlehem defeated the Philistine giant Goliath with a sling and a stone?',
      options: ['David', 'Jonathan', 'Samuel', 'Saul'],
      correctAnswer: 'David',
      acceptedAnswers: ['David', 'King David', 'Da-vid', '다윗', '大卫', '大衛'],
      explanation:
        '1 Samuel 17:49-50 records that David prevailed over the Philistine Goliath with a sling and with a stone in the Valley of Elah.',
    },
    tl: {
      questionText:
        'Sinong batang pastol mula sa Betlehem ang tumalo sa higanteng Filisteo na si Goliat gamit ang tirador at bato?',
      options: ['David', 'Jonatan', 'Samuel', 'Saul'],
      correctAnswer: 'David',
      acceptedAnswers: ['David', 'Haring David', 'Si David', 'King David'],
      explanation:
        'Ayon sa 1 Samuel 17:49-50, tinalo ni David ang Filisteong si Goliat sa pamamagitan ng isang tirador at isang bato.',
    },
    ceb: {
      questionText:
        'Kinsa nga batan-ong magbalantay sa karnero gikan sa Betlehem ang mipildi sa higanteng Filistehanon nga si Goliat pinaagi sa lambuyog ug bato?',
      options: ['David', 'Jonatan', 'Samuel', 'Saul'],
      correctAnswer: 'David',
      acceptedAnswers: ['David', 'Hari David', 'Si David', 'King David'],
      explanation:
        'Ang 1 Samuel 17:49-50 nagrekord nga si David nakadaog batok sa Filistehanon nga si Goliat pinaagi sa lambuyog ug usa ka bato.',
    },
    ko: {
      questionText:
        '물매와 돌로 블레셋 거인 골리앗을 물리친 베들레헴의 젊은 목동은 누구입니까?',
      options: ['다윗', '요나단', '사무엘', '사울'],
      correctAnswer: '다윗',
      acceptedAnswers: ['다윗', '다윗 왕', 'David', 'King David'],
      explanation:
        '사무엘상 17:49-50은 다윗이 물매와 돌로 블레셋 사람 골리앗을 이겼음을 기록합니다.',
    },
    'zh-CN': {
      questionText: '哪位来自伯利恒的年轻牧羊人用机弦和石子击败了非利士巨人歌利亚？',
      options: ['大卫', '约拿单', '撒母耳', '扫罗'],
      correctAnswer: '大卫',
      acceptedAnswers: ['大卫', '大卫王', '大衛', 'David', 'King David'],
      explanation:
        '撒母耳记上 17:49-50 记载大卫用机弦甩石胜了非利士人歌利亚。',
    },
    'zh-TW': {
      questionText: '哪位來自伯利恆的年輕牧羊人用機弦和石子擊敗了非利士巨人歌利亞？',
      options: ['大衛', '約拿單', '撒母耳', '掃羅'],
      correctAnswer: '大衛',
      acceptedAnswers: ['大衛', '大衛王', '大卫', 'David', 'King David'],
      explanation:
        '撒母耳記上 17:49-50 記載大衛用機弦甩石勝了非利士人歌利亞。',
    },
    es: {
      questionText:
        '¿Qué joven pastor de Belén derrotó al gigante filisteo Goliat con una honda y una piedra?',
      options: ['David', 'Jonatán', 'Samuel', 'Saúl'],
      correctAnswer: 'David',
      acceptedAnswers: ['David', 'Rey David', 'El rey David'],
      explanation:
        '1 Samuel 17:49-50 registra que David venció al filisteo Goliat con honda y piedra.',
    },
  },

  // 9. Short Answer (Biblical Place)
  {
    questionCode: 'QB-GOS-005',
    category: 'Miracles',
    bibleBook: 'John',
    testament: 'New Testament',
    difficulty: 'Easy',
    questionFormat: 'Short Answer',
    scriptureReference: 'John 2:1-11',
    en: {
      questionText:
        'In which Galilean town did Jesus perform His first recorded miracle of turning water into wine?',
      options: ['Cana', 'Capernaum', 'Bethany', 'Nain'],
      correctAnswer: 'Cana',
      acceptedAnswers: [
        'Cana',
        'Cana of Galilee',
        'Cana in Galilee',
        '가나',
        '갈릴리 가나',
        '迦拿',
        '加利利的迦拿',
      ],
      explanation:
        'John 2:11 states: "This beginning of miracles did Jesus in Cana of Galilee, and manifested forth his glory; and his disciples believed on him."',
    },
    tl: {
      questionText:
        'Saang bayan sa Galilea ginawa ni Hesus ang Kanyang unang himala ng paggawa ng alak mula sa tubig?',
      options: ['Cana', 'Capernaum', 'Betania', 'Nain'],
      correctAnswer: 'Cana',
      acceptedAnswers: ['Cana', 'Cana ng Galilea', 'Sa Cana', 'Cana of Galilee'],
      explanation:
        'Sa Juan 2:11, ang unang tanda at himalang ito ay ginawa ni Hesus sa Cana ng Galilea sa isang kasalan.',
    },
    ceb: {
      questionText:
        'Sa unsang lungsod sa Galilea gibuhat ni Hesus ang Iyang unang milagro sa paghimo sa tubig nga bino?',
      options: ['Cana', 'Capernaum', 'Betania', 'Nain'],
      correctAnswer: 'Cana',
      acceptedAnswers: ['Cana', 'Cana sa Galilea', 'Sa Cana', 'Cana of Galilee'],
      explanation:
        'Ang Juan 2:11 nagrekord nga kini nga sinugdanan sa mga milagro gibuhat ni Hesus didto sa Cana sa Galilea.',
    },
    ko: {
      questionText:
        '예수님께서 물을 포도주로 바꾸신 첫 번째 표적을 행하신 갈릴리의 동네 이름은 무엇입니까?',
      options: ['가나', '가버나움', '베다니', '나인'],
      correctAnswer: '가나',
      acceptedAnswers: ['가나', '갈릴리 가나', 'Cana', 'Cana of Galilee'],
      explanation:
        '요한복음 2:11은 "예수께서 이 첫 표적을 갈릴리 가나에서 행하여 그의 영광을 나타내시매 제자들이 그를 믿으니라"고 기록합니다.',
    },
    'zh-CN': {
      questionText: '耶稣在加利利的哪个城镇行了头一件神迹，将水变为酒？',
      options: ['迦拿', '迦百农', '伯大尼', '拿因'],
      correctAnswer: '迦拿',
      acceptedAnswers: ['迦拿', '加利利的迦拿', 'Cana'],
      explanation:
        '约翰福音 2:11 记载：“这是耶稣所行的头一件神迹，是在加利利的迦拿行的，显出他的荣耀来，他的门徒就信他了。”',
    },
    'zh-TW': {
      questionText: '耶穌在加利利的哪個城鎮行了頭一件神蹟，將水變為酒？',
      options: ['迦拿', '迦百農', '伯大尼', '拿因'],
      correctAnswer: '迦拿',
      acceptedAnswers: ['迦拿', '加利利的迦拿', 'Cana'],
      explanation:
        '約翰福音 2:11 記載：「這是耶穌所行的頭一件神蹟，是在加利利的迦拿行的，顯出他的榮耀來，他的門徒就信他了。」',
    },
    es: {
      questionText:
        '¿En qué pueblo de Galilea realizó Jesús su primer milagro registrado al convertir el agua en vino?',
      options: ['Caná', 'Capernaum', 'Betania', 'Naín'],
      correctAnswer: 'Caná',
      acceptedAnswers: ['Caná', 'Cana', 'Caná de Galilea'],
      explanation:
        'Juan 2:11 declara: "Este principio de señales hizo Jesús en Caná de Galilea, y manifestó su gloria."',
    },
  },

  // 10. Short Answer (Biblical Book)
  {
    questionCode: 'QB-REV-011',
    category: 'Revelation',
    bibleBook: 'Revelation',
    testament: 'New Testament',
    difficulty: 'Intermediate',
    questionFormat: 'Short Answer',
    scriptureReference: 'Revelation 1:9-11',
    en: {
      questionText:
        'Which final book of the New Testament was written by John on the island of Patmos and addressed to seven churches in Asia?',
      options: ['Revelation', 'Hebrews', '1 John', 'Colossians'],
      correctAnswer: 'Revelation',
      acceptedAnswers: [
        'Revelation',
        'Book of Revelation',
        'Revelations',
        'Apocalypse',
        'Pahayag',
        'Gipadayag',
        'Pinadayag',
        '요한계시록',
        '계시록',
        '启示录',
        '啟示錄',
      ],
      explanation:
        'Revelation 1:9-11 records John on the isle called Patmos receiving the command to write to the seven churches: Ephesus, Smyrna, Pergamos, Thyatira, Sardis, Philadelphia, and Laodicea.',
    },
    tl: {
      questionText:
        'Aling huling aklat sa Bagong Tipan ang isinulat ni Juan sa pulo ng Patmos at ipinadala sa pitong iglesya sa Asia?',
      options: ['Pahayag', 'Mga Hebreo', '1 Juan', 'Mga Taga-Colosas'],
      correctAnswer: 'Pahayag',
      acceptedAnswers: ['Pahayag', 'Apocalipsis', 'Pahayag (Apocalipsis)', 'Revelation'],
      explanation:
        'Sa Pahayag 1:9-11, si Juan ay nasa pulo ng Patmos nang utusang sumulat sa pitong iglesya sa Asia.',
    },
    ceb: {
      questionText:
        'Unsang katapusang basahon sa Bag-ong Tugon ang gisulat ni Juan sa pulo sa Patmos ug gipadala ngadto sa pito ka iglesia sa Asia?',
      options: ['Gipadayag', 'Mga Hebreohanon', '1 Juan', 'Mga Taga-Colosas'],
      correctAnswer: 'Gipadayag',
      acceptedAnswers: ['Gipadayag', 'Pinadayag', 'Gipadayag (Pinadayag)', 'Apocalipsis', 'Revelation'],
      explanation:
        'Ang Gipadayag 1:9-11 naghisgot kang Juan sa pulo sa Patmos nga gisugo sa pagsulat ngadto sa pito ka iglesia sa Asia.',
    },
    ko: {
      questionText:
        '사도 요한이 밧모 섬에서 기록하여 아시아의 일곱 교회에 보낸 신약 성경의 마지막 책 이름은 무엇입니까?',
      options: ['요한계시록', '히브리서', '요한일서', '골로새서'],
      correctAnswer: '요한계시록',
      acceptedAnswers: ['요한계시록', '계시록', '요한 계시록', 'Revelation'],
      explanation:
        '요한계시록 1:9-11은 요한이 밧모라 하는 섬에서 에베소, 서머나, 버가모, 두아디라, 사데, 빌라델비아, 라오디게아 등 일곱 교회에 편지하라는 명령을 받은 것을 기록합니다.',
    },
    'zh-CN': {
      questionText: '新约圣经的最后一卷书是由约翰在拔摩海岛上所写、寄给亚细亚七个教会的，这卷书叫什么？',
      options: ['启示录', '希伯来书', '约翰一书', '歌罗西书'],
      correctAnswer: '启示录',
      acceptedAnswers: ['启示录', '约翰启示录', '啟示錄', 'Revelation'],
      explanation:
        '启示录 1:9-11 记载约翰在拔摩海岛上受命写信给亚细亚的七个教会。',
    },
    'zh-TW': {
      questionText: '新約聖經的最後一卷書是由約翰在拔摩海島上所寫、寄給亞細亞七個教會的，這卷書叫什麼？',
      options: ['啟示錄', '希伯來書', '約翰一書', '歌羅西書'],
      correctAnswer: '啟示錄',
      acceptedAnswers: ['啟示錄', '約翰啟示錄', '启示录', 'Revelation'],
      explanation:
        '啟示錄 1:9-11 記載約翰在拔摩海島上受命寫信給亞細亞的七個教會。',
    },
    es: {
      questionText:
        '¿Qué último libro del Nuevo Testamento fue escrito por Juan en la isla de Patmos y dirigido a las siete iglesias de Asia?',
      options: ['Apocalipsis', 'Hebreos', '1 Juan', 'Colosenses'],
      correctAnswer: 'Apocalipsis',
      acceptedAnswers: ['Apocalipsis', 'Revelación', 'Revelation'],
      explanation:
        'Apocalipsis 1:9-11 registra a Juan en la isla llamada Patmos recibiendo el mandato de escribir a las siete iglesias de Asia.',
    },
  },

  // 11. Matching (Armor of God - Ephesians 6:14-17)
  {
    questionCode: 'QB-PAU-010',
    category: 'Pauline Epistles',
    bibleBook: 'Ephesians',
    testament: 'New Testament',
    difficulty: 'Intermediate',
    questionFormat: 'Matching',
    scriptureReference: 'Ephesians 6:14-17',
    en: {
      questionText:
        'Match each piece of the Armor of God in Ephesians 6:14-17 with the spiritual truth it represents:',
      options: [
        'Word of God',
        'Faith',
        'Righteousness',
        'Salvation',
      ],
      correctAnswer: 'Sword -> Word of God | Shield -> Faith | Breastplate -> Righteousness | Helmet -> Salvation',
      matchingPairs: [
        { left: 'Sword of the Spirit', right: 'Word of God' },
        { left: 'Shield', right: 'Faith' },
        { left: 'Breastplate', right: 'Righteousness' },
        { left: 'Helmet', right: 'Salvation' },
      ],
      explanation:
        'Ephesians 6:14-17 describes the breastplate of righteousness, the shield of faith, the helmet of salvation, and the sword of the Spirit, which is the word of God.',
    },
    tl: {
      questionText:
        'Itugma ang bawat bahagi ng Baluti ng Diyos sa Efeso 6:14-17 sa espirituwal na katotohanang kinakatawan nito:',
      options: [
        'Salita ng Diyos',
        'Pananampalataya',
        'Katuwiran',
        'Kaligtasan',
      ],
      correctAnswer: 'Tabak -> Salita ng Diyos | Kalasag -> Pananampalataya | Baluti -> Katuwiran | Turbante -> Kaligtasan',
      matchingPairs: [
        { left: 'Tabak ng Espiritu', right: 'Salita ng Diyos' },
        { left: 'Kalasag', right: 'Pananampalataya' },
        { left: 'Baluti sa Dibdib', right: 'Katuwiran' },
        { left: 'Turbante (Helmet)', right: 'Kaligtasan' },
      ],
      explanation:
        'Inilalarawan sa Efeso 6:14-17 ang baluti ng katuwiran, kalasag ng pananampalataya, turbante ng kaligtasan, at tabak ng Espiritu na siyang salita ng Diyos.',
    },
    ceb: {
      questionText:
        'Ipares ang matag bahin sa Hinagiban sa Diyos sa Efeso 6:14-17 ngadto sa espirituhanong kamatuoran nga girepresentar niini:',
      options: [
        'Pulong sa Diyos',
        'Pagtuo',
        'Pagkamatarong',
        'Kaluwasan',
      ],
      correctAnswer: 'Espada -> Pulong sa Diyos | Taming -> Pagtuo | Kotamaya -> Pagkamatarong | Salukot -> Kaluwasan',
      matchingPairs: [
        { left: 'Espada sa Espiritu', right: 'Pulong sa Diyos' },
        { left: 'Taming', right: 'Pagtuo' },
        { left: 'Kotamaya saDughan', right: 'Pagkamatarong' },
        { left: 'Salukot (Helmet)', right: 'Kaluwasan' },
      ],
      explanation:
        'Ang Efeso 6:14-17 naghulagway sa kotamaya sa pagkamatarong, taming sa pagtuo, salukot sa kaluwasan, ug espada sa Espiritu nga mao ang pulong sa Diyos.',
    },
    ko: {
      questionText:
        '에베소서 6:14-17에 나오는 하나님의 전신갑주와 그것이 상징하는 영적 진리를 바르게 짝지으세요:',
      options: ['하나님의 말씀', '믿음', '의 (Righteousness)', '구원'],
      correctAnswer: '성령의 검 -> 하나님의 말씀 | 방패 -> 믿음 | 흉배 -> 의 | 투구 -> 구원',
      matchingPairs: [
        { left: '성령의 검', right: '하나님의 말씀' },
        { left: '방패', right: '믿음' },
        { left: '호심경 (흉배)', right: '의 (Righteousness)' },
        { left: '투구', right: '구원' },
      ],
      explanation:
        '에베소서 6:14-17은 의의 호심경, 믿음의 방패, 구원의 투구, 그리고 하나님의 말씀인 성령의 검을 기록하고 있습니다.',
    },
    'zh-CN': {
      questionText: '请将以弗所书 6:14-17 中神所赐的全副军装与它们所代表的属灵真理配对：',
      options: ['神的道', '信德（信心）', '公义', '救恩'],
      correctAnswer: '圣灵的宝剑 -> 神的道 | 藤牌 -> 信德（信心） | 护心镜 -> 公义 | 头盔 -> 救恩',
      matchingPairs: [
        { left: '圣灵的宝剑', right: '神的道' },
        { left: '盾牌（藤牌）', right: '信德（信心）' },
        { left: '护心镜', right: '公义' },
        { left: '头盔', right: '救恩' },
      ],
      explanation:
        '以弗所书 6:14-17 描述了公义的护心镜、信德的藤牌、救恩的头盔，以及圣灵的宝剑（就是神的道）。',
    },
    'zh-TW': {
      questionText: '請將以弗所書 6:14-17 中神所賜的全副軍裝與它們所代表的屬靈真理配對：',
      options: ['神的道', '信德（信心）', '公義', '救恩'],
      correctAnswer: '聖靈的寶劍 -> 神的道 | 藤牌 -> 信德（信心） | 護心鏡 -> 公義 | 頭盔 -> 救恩',
      matchingPairs: [
        { left: '聖靈的寶劍', right: '神的道' },
        { left: '盾牌（藤牌）', right: '信德（信心）' },
        { left: '護心鏡', right: '公義' },
        { left: '頭盔', right: '救恩' },
      ],
      explanation:
        '以弗所書 6:14-17 描述了公義的護心鏡、信德的藤牌、救恩的頭盔，以及聖靈的寶劍（就是神的道）。',
    },
    es: {
      questionText:
        'Empareje cada pieza de la Armadura de Dios en Efesios 6:14-17 con la verdad espiritual que representa:',
      options: ['Palabra de Dios', 'Fe', 'Justicia', 'Salvación'],
      correctAnswer: 'Espada -> Palabra de Dios | Escudo -> Fe | Coraza -> Justicia | Yelmo -> Salvación',
      matchingPairs: [
        { left: 'Espada del Espíritu', right: 'Palabra de Dios' },
        { left: 'Escudo', right: 'Fe' },
        { left: 'Coraza', right: 'Justicia' },
        { left: 'Yelmo', right: 'Salvación' },
      ],
      explanation:
        'Efesios 6:14-17 describe la coraza de justicia, el escudo de la fe, el yelmo de la salvación y la espada del Espíritu, que es la palabra de Dios.',
    },
  },

  // 12. Matching (Biblical Person -> Event)
  {
    questionCode: 'QB-MAT-017',
    category: 'Biblical Characters',
    bibleBook: 'Hebrews',
    testament: 'New Testament',
    difficulty: 'Easy',
    questionFormat: 'Matching',
    scriptureReference: 'Hebrews 11:7-29',
    en: {
      questionText:
        'Match each biblical figure mentioned in Hebrews 11 with their corresponding event in Scripture:',
      options: [
        'Built an ark to save his household',
        'Parted and crossed the Red Sea',
        'Walls of Jericho fell down after seven days',
        'Left Ur of the Chaldees by faith',
      ],
      correctAnswer: 'Noah -> Built an ark | Moses -> Crossed the Red Sea | Joshua -> Walls of Jericho | Abraham -> Left Ur',
      matchingPairs: [
        { left: 'Noah', right: 'Built an ark to save his household' },
        { left: 'Moses', right: 'Parted and crossed the Red Sea' },
        { left: 'Joshua', right: 'Walls of Jericho fell down after seven days' },
        { left: 'Abraham', right: 'Left Ur of the Chaldees by faith' },
      ],
      explanation:
        'Hebrews 11 recounts the faith of Noah (v.7), Abraham (v.8), Moses at the Red Sea (v.29), and Joshua at the walls of Jericho (v.30).',
    },
    tl: {
      questionText:
        'Itugma ang bawat tauhan sa Bibliya na binanggit sa Hebreo 11 sa kanilang kaukulang pangyayari sa Kasulatan:',
      options: [
        'Nagpatayo ng daong para sa kanyang sambahayan',
        'Tumawid sa Dagat na Pula',
        'Bumagsak ang pader ng Jerico matapos ang 7 araw',
        'Umalis sa Ur ng mga Caldeo sa pananampalataya',
      ],
      correctAnswer: 'Noe -> Daong | Moises -> Dagat na Pula | Josue -> Pader ng Jerico | Abraham -> Umalis sa Ur',
      matchingPairs: [
        { left: 'Noe', right: 'Nagpatayo ng daong para sa kanyang sambahayan' },
        { left: 'Moises', right: 'Tumawid sa Dagat na Pula' },
        { left: 'Josue', right: 'Bumagsak ang pader ng Jerico matapos ang 7 araw' },
        { left: 'Abraham', right: 'Umalis sa Ur ng mga Caldeo sa pananampalataya' },
      ],
      explanation:
        'Inilalahad sa Hebreo 11 ang pananampalataya nina Noe, Abraham, Moises sa Dagat na Pula, at Josue sa Jerico.',
    },
    ceb: {
      questionText:
        'Ipares ang matag karakter sa Bibliya sa Hebreohanon 11 ngadto sa ilang hitabo sa Kasulatan:',
      options: [
        'Nagbuhat og arka alang sa iyang panimalay',
        'Mitabok sa Pulang Dagat',
        'Nahugno ang paril sa Jerico human sa 7 ka adlaw',
        'Mibiya sa Ur sa mga Caldeo pinaagi sa pagtuo',
      ],
      correctAnswer: 'Noe -> Arka | Moises -> Pulang Dagat | Josue -> Paril sa Jerico | Abraham -> Ur',
      matchingPairs: [
        { left: 'Noe', right: 'Nagbuhat og arka alang sa iyang panimalay' },
        { left: 'Moises', right: 'Mitabok sa Pulang Dagat' },
        { left: 'Josue', right: 'Nahugno ang paril sa Jerico human sa 7 ka adlaw' },
        { left: 'Abraham', right: 'Mibiya sa Ur sa mga Caldeo pinaagi sa pagtuo' },
      ],
      explanation:
        'Ang Hebreohanon 11 naghisgot sa pagtuo nila ni Noe, Abraham, Moises sa Pulang Dagat, ug Josue sa Jerico.',
    },
    ko: {
      questionText:
        '히브리서 11장에 기록된 성경 인물과 그에 해당하는 성경적 사건을 바르게 연결하세요:',
      options: [
        '방주를 준비하여 자기 집을 구원함',
        '홍해를 육지같이 건넘',
        '칠 일 동안 돌매 여리고 성이 무너짐',
        '믿음으로 갈대아 우르를 떠나 순종함',
      ],
      correctAnswer: '노아 -> 방주 | 모세 -> 홍해 | 여호수아 -> 여리고 성 | 아브라함 -> 갈대아 우르',
      matchingPairs: [
        { left: '노아', right: '방주를 준비하여 자기 집을 구원함' },
        { left: '모세', right: '홍해를 육지같이 건넘' },
        { left: '여호수아', right: '칠 일 동안 돌매 여리고 성이 무너짐' },
        { left: '아브라함', right: '믿음으로 갈대아 우르를 떠나 순종함' },
      ],
      explanation:
        '히브리서 11장은 노아(7절), 아브라함(8절), 홍해를 건넌 모세(29절), 여리고 성이 무너진 사건(30절)의 믿음을 증언합니다.',
    },
    'zh-CN': {
      questionText: '请将希伯来书第 11 章提到的圣经人物与对应的圣经事件配对：',
      options: [
        '预备方舟使全家得救',
        '过红海如行干地',
        '围绕七日后耶利哥城墙倒塌',
        '因着信离开迦勒底的吾珥',
      ],
      correctAnswer: '挪亚 -> 方舟 | 摩西 -> 过红海 | 约书亚 -> 耶利哥城 | 亚伯拉罕 -> 离开吾珥',
      matchingPairs: [
        { left: '挪亚', right: '预备方舟使全家得救' },
        { left: '摩西', right: '过红海如行干地' },
        { left: '约书亚', right: '围绕七日后耶利哥城墙倒塌' },
        { left: '亚伯拉罕', right: '因着信离开迦勒底的吾珥' },
      ],
      explanation:
        '希伯来书 11 章见证了挪亚（7节）、亚伯拉罕（8节）、摩西过红海（29节）以及耶利哥城倒塌（30节）的信心。',
    },
    'zh-TW': {
      questionText: '請將希伯來書第 11 章提到的聖經人物與對應的聖經事件配對：',
      options: [
        '預備方舟使全家得救',
        '過紅海如行乾地',
        '圍繞七日後耶利哥城牆倒塌',
        '因著信離開迦勒底的吾珥',
      ],
      correctAnswer: '挪亞 -> 方舟 | 摩西 -> 過紅海 | 約書亞 -> 耶利哥城 | 亞伯拉罕 -> 離開吾珥',
      matchingPairs: [
        { left: '挪亞', right: '預備方舟使全家得救' },
        { left: '摩西', right: '過紅海如行乾地' },
        { left: '約書亞', right: '圍繞七日後耶利哥城牆倒塌' },
        { left: '亞伯拉罕', right: '因著信離開迦勒底的吾珥' },
      ],
      explanation:
        '希伯來書 11 章見證了挪亞（7節）、亞伯拉罕（8節）、摩西過紅海（29節）以及耶利哥城倒塌（30節）的信心。',
    },
    es: {
      questionText:
        'Empareje cada personaje bíblico mencionado en Hebreos 11 con su evento correspondiente:',
      options: [
        'Preparó el arca para salvar su casa',
        'Cruzó el Mar Rojo como por tierra seca',
        'Los muros de Jericó cayeron tras siete días',
        'Salió de Ur de los caldeos por la fe',
      ],
      correctAnswer: 'Noé -> Arca | Moisés -> Mar Rojo | Josué -> Jericó | Abraham -> Ur',
      matchingPairs: [
        { left: 'Noé', right: 'Preparó el arca para salvar su casa' },
        { left: 'Moisés', right: 'Cruzó el Mar Rojo como por tierra seca' },
        { left: 'Josué', right: 'Los muros de Jericó cayeron tras siete días' },
        { left: 'Abraham', right: 'Salió de Ur de los caldeos por la fe' },
      ],
      explanation:
        'Hebreos 11 relata la fe de Noé, Abraham, Moisés en el Mar Rojo y Josué en Jericó.',
    },
  },

  // 13. Multiple Choice (Acts)
  {
    questionCode: 'QB-ACT-006',
    category: 'Acts',
    bibleBook: 'Acts',
    testament: 'New Testament',
    difficulty: 'Intermediate',
    questionFormat: 'Multiple Choice',
    scriptureReference: 'Acts 11:26',
    en: {
      questionText: 'In which city were the disciples first called Christians?',
      options: ['Antioch', 'Jerusalem', 'Ephesus', 'Corinth'],
      correctAnswer: 'Antioch',
      explanation:
        'Acts 11:26 records that Barnabas and Saul assembled with the church in Antioch for a whole year, "and the disciples were called Christians first in Antioch."',
    },
    tl: {
      questionText:
        'Saang lungsod unang tinawag na mga Kristiyano ang mga alagad?',
      options: ['Antioquia', 'Jerusalem', 'Efeso', 'Corinto'],
      correctAnswer: 'Antioquia',
      explanation:
        'Nakatala sa Mga Gawa 11:26 na sa Antioquia unang tinawag na mga Kristiyano ang mga alagad.',
    },
    ceb: {
      questionText:
        'Sa unsang siyudad unang gitawag nga mga Kristohanon ang mga tinun-an?',
      options: ['Antioquia', 'Jerusalem', 'Efeso', 'Corinto'],
      correctAnswer: 'Antioquia',
      explanation:
        'Ang Buhat 11:26 nag-ingon nga didto sa Antioquia ang mga tinun-an unang gitawag nga mga Kristohanon.',
    },
    ko: {
      questionText:
        '제자들이 처음으로 "그리스도인"이라 일컬음을 받은 도시는 어디입니까?',
      options: ['안디옥', '예루살렘', '에베소', '고린도'],
      correctAnswer: '안디옥',
      explanation:
        '사도행전 11:26은 바나바와 사울이 일 년간 모여 큰 무리를 가르쳤고 "제자들이 안디옥에서 비로소 그리스도인이라 일컬음을 받게 되었더라"고 기록합니다.',
    },
    'zh-CN': {
      questionText: '门徒称为“基督徒”是从哪座城市起首的？',
      options: ['安提阿', '耶路撒冷', '以弗所', '哥林多'],
      correctAnswer: '安提阿',
      explanation:
        '使徒行传 11:26 记载巴拿巴和扫罗在教会里足有一年的工夫教训许多人，“门徒称为基督徒是从安提阿起首。”',
    },
    'zh-TW': {
      questionText: '門徒稱為「基督徒」是從哪座城市起首的？',
      options: ['安提阿', '耶路撒冷', '以弗所', '哥林多'],
      correctAnswer: '安提阿',
      explanation:
        '使徒行傳 11:26 記載巴拿巴和掃羅在教會裡足有一年的工夫教訓許多人，「門徒稱為基督徒是從安提阿起首。」',
    },
    es: {
      questionText:
        '¿En qué ciudad se llamó por primera vez cristianos a los discípulos?',
      options: ['Antioquía', 'Jerusalén', 'Éfeso', 'Corinto'],
      correctAnswer: 'Antioquía',
      explanation:
        'Hechos 11:26 registra que a los discípulos se les llamó cristianos por primera vez en Antioquía.',
    },
  },

  // 14. Multiple Choice (Biblical Chronology)
  {
    questionCode: 'QB-CHR-007',
    category: 'Biblical History',
    bibleBook: 'Genesis',
    testament: 'Old Testament',
    difficulty: 'Intermediate',
    questionFormat: 'Multiple Choice',
    scriptureReference: 'Genesis 12:1',
    en: {
      questionText:
        'Which of these Old Testament events occurred FIRST in biblical chronology?',
      options: [
        'The Call of Abram from Ur of the Chaldees',
        'The Giving of the Law at Mount Sinai',
        'David Anointed by Samuel in Bethlehem',
        'Solomon Dedicating the First Temple',
      ],
      correctAnswer: 'The Call of Abram from Ur of the Chaldees',
      explanation:
        "God called Abram in Genesis 12, centuries before the Exodus and Mount Sinai (Exodus 19-20), King David (1 Samuel 16), and Solomon's Temple (1 Kings 8).",
    },
    tl: {
      questionText:
        'Alin sa mga pangyayaring ito sa Lumang Tipan ang UNANG naganap sa pagkakasunod-sunod ng Bibliya?',
      options: [
        'Ang Pagtawag kay Abram mula sa Ur ng mga Caldeo',
        'Ang Pagbibigay ng Kautusan sa Bundok Sinai',
        'Ang Pagpapahid ng Langis ni Samuel kay David',
        'Ang Paghahandog ni Solomon sa Unang Templo',
      ],
      correctAnswer: 'Ang Pagtawag kay Abram mula sa Ur ng mga Caldeo',
      explanation:
        'Ang pagtawag kay Abram (Genesis 12) ay nauna nang maraming siglo bago ang Exodo sa Sinai, paghahari ni David, at pagtatayo ng Templo ni Solomon.',
    },
    ceb: {
      questionText:
        'Hain niining mga hitabo sa Daang Tugon ang UNANG nahitabo sa han-ay sa Bibliya?',
      options: [
        'Ang Pagtawag kang Abram gikan sa Ur sa mga Caldeo',
        'Ang Paghatag sa Balaod sa Bukid sa Sinai',
        'Ang Pagdihog ni Samuel kang David sa Bethlehem',
        'Ang Paghalad ni Solomon sa Unang Templo',
      ],
      correctAnswer: 'Ang Pagtawag kang Abram gikan sa Ur sa mga Caldeo',
      explanation:
        'Ang pagtawag kang Abram (Genesis 12) nahitabo pag-una sa wala pa ang Exodo, ang paghari ni David, ug ang Templo ni Solomon.',
    },
    ko: {
      questionText:
        '다음 구약 성경의 사건 중 성경 연대기상 가장 먼저 일어난 사건은 무엇입니까?',
      options: [
        '갈대아 우르에서 아브람을 부르심',
        '시내산에서 율법을 주심',
        '사무엘이 베들레헴에서 다윗에게 기름을 부음',
        '솔로몬이 제1성전을 봉헌함',
      ],
      correctAnswer: '갈대아 우르에서 아브람을 부르심',
      explanation:
        '창세기 12장의 아브람 소명 사건은 출애굽과 시내산 언약(출 19-20장), 다윗의 기름부음(삼상 16장), 솔로몬 성전 봉헌(왕상 8장)보다 수백 년 앞서 일어났습니다.',
    },
    'zh-CN': {
      questionText: '按照圣经年代顺序，以下哪件旧约事件最早发生？',
      options: [
        '神呼召亚伯兰离开迦勒底的吾珥',
        '西乃山颁布律法',
        '撒母耳在伯利恒膏立大卫',
        '所罗门奉献第一圣殿',
      ],
      correctAnswer: '神呼召亚伯兰离开迦勒底的吾珥',
      explanation:
        '创世记第 12 章神呼召亚伯兰，早于出埃及与西乃山之约（出 19-20 章）、大卫受膏（撒上 16 章）和所罗门建殿（王上 8 章）。',
    },
    'zh-TW': {
      questionText: '按照聖經年代順序，以下哪件舊約事件最早發生？',
      options: [
        '神呼召亞伯蘭離開迦勒底的吾珥',
        '西乃山頒布律法',
        '撒母耳在伯利恆膏立大衛',
        '所羅門奉獻第一聖殿',
      ],
      correctAnswer: '神呼召亞伯蘭離開迦勒底的吾珥',
      explanation:
        '創世記第 12 章神呼召亞伯蘭，早於出埃及與西乃山之約（出 19-20 章）、大衛受膏（撒上 16 章）和所羅門建殿（王上 8 章）。',
    },
    es: {
      questionText:
        '¿Cuál de estos eventos del Antiguo Testamento ocurrió PRIMERO en la cronología bíblica?',
      options: [
        'El llamado de Abram desde Ur de los caldeos',
        'La entrega de la Ley en el Monte Sinaí',
        'David ungido por Samuel en Belén',
        'Salomón dedicando el Primer Templo',
      ],
      correctAnswer: 'El llamado de Abram desde Ur de los caldeos',
      explanation:
        'Dios llamó a Abram en Génesis 12, siglos antes del Éxodo en Sinaí, el rey David y el Templo de Salomón.',
    },
  },

  // 15. Multiple Choice (Ruth)
  {
    questionCode: 'QB-WHO-008',
    category: 'Women of the Bible',
    bibleBook: 'Ruth',
    testament: 'Old Testament',
    difficulty: 'Easy',
    questionFormat: 'Multiple Choice',
    scriptureReference: 'Ruth 1:16',
    en: {
      questionText:
        'Who spoke these words: "Whither thou goest, I will go; and where thou lodgest, I will lodge: thy people shall be my people, and thy God my God"?',
      options: [
        'Ruth to Naomi',
        'Esther to Mordecai',
        'Rebekah to Isaac',
        'Rachel to Jacob',
      ],
      correctAnswer: 'Ruth to Naomi',
      explanation:
        'In Ruth 1:16, Ruth the Moabitess declared her steadfast loyalty to her mother-in-law Naomi and to the God of Israel.',
    },
    tl: {
      questionText:
        'Sino ang nagsabi nito: "Kung saan ka paroroon ay paroroon ako; ang iyong bayan ay magiging aking bayan, at ang iyong Diyos ay aking Diyos"?',
      options: [
        'Si Ruth kay Noemi',
        'Si Ester kay Mardocheo',
        'Si Rebeca kay Isaac',
        'Si Raquel kay Jacob',
      ],
      correctAnswer: 'Si Ruth kay Noemi',
      explanation:
        'Sa Ruth 1:16, ipinahayag ni Ruth ang kanyang tapat na paninindigan sa kanyang biyenang si Noemi at sa Diyos ng Israel.',
    },
    ceb: {
      questionText:
        'Kinsa ang misulti niini: "Asa ikaw moadto, ako moadto; ang imong katawhan mahimong akong katawhan, ug ang imong Diyos akong Diyos"?',
      options: [
        'Si Ruth ngadto kang Noemi',
        'Si Ester ngadto kang Mardokeo',
        'Si Rebeca ngadto kang Isaac',
        'Si Raquel ngadto kang Jacob',
      ],
      correctAnswer: 'Si Ruth ngadto kang Noemi',
      explanation:
        'Sa Ruth 1:16, gipadayag ni Ruth ang iyang pagkamaunongon ngadto sa iyang ugangan nga si Noemi ug sa Diyos sa Israel.',
    },
    ko: {
      questionText:
        '"어머니께서 가시는 곳에 나도 가고 어머니께서 머무시는 곳에서 나도 머물겠나이다 어머니의 백성이 나의 백성이 되고 어머니의 하나님이 나의 하나님이 되시리니"라고 고백한 사람은 누구입니까?',
      options: ['룻이 나오미에게', '에스더가 모르드개에게', '리브가가 이삭에게', '라헬이 야곱에게'],
      correctAnswer: '룻이 나오미에게',
      explanation:
        '룻기 1:16에서 모압 여인 룻은 시어머니 나오미와 이스라엘의 하나님께 대한 변함없는 헌신을 고백했습니다.',
    },
    'zh-CN': {
      questionText:
        '谁说了这段话：“你往哪里去，我也往那里去；你在哪里住宿，我也在那里住宿；你的国就是我的国，你的神就是我的神”？',
      options: ['路得对拿俄米', '以斯帖对末底改', '利百加对以撒', '拉结对雅各'],
      correctAnswer: '路得对拿俄米',
      explanation:
        '在路得记 1:16 中，摩押女子路得向婆婆拿俄米表明她坚定跟随以色列神的决心。',
    },
    'zh-TW': {
      questionText:
        '誰說了這段話：「你往哪裡去，我也往那裡去；你在哪裡住宿，我也在那裡住宿；你的國就是我的國，你的神就是我的神」？',
      options: ['路得對拿俄米', '以斯帖對末底改', '利百加對以撒', '拉結對雅各'],
      correctAnswer: '路得對拿俄米',
      explanation:
        '在路得記 1:16 中，摩押女子路得向婆婆拿俄米表明她堅定跟隨以色列神的決心。',
    },
    es: {
      questionText:
        '¿Quién dijo: "No me ruegues que te deje... porque a dondequiera que tú fueres, iré yo, y dondequiera que vivieres, viviré. Tu pueblo será mi pueblo, y tu Dios mi Dios"?',
      options: ['Rut a Noemí', 'Ester a Mardoqueo', 'Rebeca a Isaac', 'Raquel a Jacob'],
      correctAnswer: 'Rut a Noemí',
      explanation:
        'En Rut 1:16, Rut la moabita declaró su lealtad inquebrantable a su suegra Noemí y al Dios de Israel.',
    },
  },

  // 16. Multiple Choice (Samson's Riddle)
  {
    questionCode: 'QB-RID-009',
    category: 'Biblical Characters',
    bibleBook: 'Judges',
    testament: 'Old Testament',
    difficulty: 'Advanced',
    questionFormat: 'Multiple Choice',
    scriptureReference: 'Judges 14:14',
    en: {
      questionText:
        'Which judge of Israel posed the riddle: "Out of the eater came forth meat, and out of the strong came forth sweetness"?',
      options: [
        'Samson — bees and honey inside a lion\'s carcass',
        'Gideon — dew on a wool fleece on the threshing floor',
        'Jephthah — olive oil pressed in Gilead',
        'Othniel — figs from the valley of Hebron',
      ],
      correctAnswer: 'Samson — bees and honey inside a lion\'s carcass',
      explanation:
        'In Judges 14:8-14, Samson found a swarm of bees and honey in the carcass of the lion he had torn, prompting his famous wedding riddle.',
    },
    tl: {
      questionText:
        'Sinong hukom ng Israel ang nagbigay ng bugtong: "Mula sa mangangain ay lumabas ang pagkain, at mula sa malakas ay lumabas ang katamisan"?',
      options: [
        'Samson — pulut-pukyutan sa loob ng bangkay ng leon',
        'Gideon — hamog sa balahibo ng tupa sa giikan',
        'Jefte — langis ng olibo mula sa Galaad',
        'Otniel — igos mula sa libis ng Hebron',
      ],
      correctAnswer: 'Samson — pulut-pukyutan sa loob ng bangkay ng leon',
      explanation:
        'Sa Hukom 14:8-14, nakakita si Samson ng kawan ng mga pukyutan at pulot sa bangkay ng leon, na naging batayan ng kanyang bugtong.',
    },
    ceb: {
      questionText:
        'Kinsa nga maghuhukom sa Israel ang naghatag sa tigmo: "Gikan sa magkakaon migula ang kalan-on, ug gikan sa kusgan migula ang katam-is"?',
      options: [
        'Samson — dugos sa buyog sulod sa patayng lawas sa leon',
        'Gideon — yamog sa balhibo sa karnero',
        'Jepte — lana sa olibo sa Galaad',
        'Otniel — igos gikan sa walog sa Hebron',
      ],
      correctAnswer: 'Samson — dugos sa buyog sulod sa patayng lawas sa leon',
      explanation:
        'Sa Maghuhukom 14:8-14, nakakaplag si Samson og dugos ug mga buyog sa kaha sa leon, nga maoy hinungdan sa iyang tigmo.',
    },
    ko: {
      questionText:
        '"먹는 자에게서 먹는 것이 나오고 강하고 매운 자에게서 단 것이 나왔느니라"라는 수수께끼를 낸 사사는 누구입니까?',
      options: [
        '삼손 — 사자의 주검 속에 있던 벌떼와 꿀',
        '기드온 — 타작마당 양털에 내린 이슬',
        '입다 — 길르앗의 감람유',
        '옷니엘 — 헤브론 골짜기의 무화과',
      ],
      correctAnswer: '삼손 — 사자의 주검 속에 있던 벌떼와 꿀',
      explanation:
        '사사기 14:8-14에서 삼손은 자신이 찢어 죽인 사자의 시체 속에 벌떼와 꿀이 있는 것을 보고 혼인 잔치에서 이 수수께끼를 냈습니다.',
    },
    'zh-CN': {
      questionText:
        '哪位以色列士师提出了这个谜语：“吃的从吃者出来，甜的从强者出来”？',
      options: [
        '参孙 —— 死狮之内有蜜蜂和蜂蜜',
        '基甸 —— 禾场上羊毛上的露水',
        '耶弗他 —— 基列榨出的橄榄油',
        '俄陀聂 —— 希伯仑谷的无花果',
      ],
      correctAnswer: '参孙 —— 死狮之内有蜜蜂和蜂蜜',
      explanation:
        '在士师记 14:8-14 中，参孙看见死狮之内有一群蜜蜂与蜂蜜，因此设了这个著名的婚宴谜语。',
    },
    'zh-TW': {
      questionText:
        '哪位以色列士師提出了這個謎語：「吃的從吃者出來，甜的從強者出來」？',
      options: [
        '參孫 —— 死獅之內有蜜蜂和蜂蜜',
        '基甸 —— 禾場上羊毛上的露水',
        '耶弗他 —— 基列榨出的橄欖油',
        '俄陀聶 —— 希伯崙谷的無花果',
      ],
      correctAnswer: '參孫 —— 死獅之內有蜜蜂和蜂蜜',
      explanation:
        '在士師記 14:8-14 中，參孫看見死獅之內有一群蜜蜂與蜂蜜，因此設了這個著名的婚宴謎語。',
    },
    es: {
      questionText:
        '¿Qué juez de Israel propuso el enigma: "Del devorador salió comida, y del fuerte salió dulzura"?',
      options: [
        'Sansón — abejas y miel dentro del cuerpo del león',
        'Gedeón — rocío sobre el vellón de lana',
        'Jefté — aceite de oliva en Galaad',
        'Otoniel — higos del valle de Hebrón',
      ],
      correctAnswer: 'Sansón — abejas y miel dentro del cuerpo del león',
      explanation:
        'En Jueces 14:8-14, Sansón halló un enjambre de abejas y miel en el cuerpo del león, lo que inspiró su enigma.',
    },
  },

  // 17. Multiple Choice (Daniel 5)
  {
    questionCode: 'QB-PRO-012',
    category: 'Major Prophets',
    bibleBook: 'Daniel',
    testament: 'Old Testament',
    difficulty: 'Expert',
    questionFormat: 'Multiple Choice',
    scriptureReference: 'Daniel 5:25-28',
    en: {
      questionText:
        "In Daniel 5:25-28, what words were inscribed on the palace wall during King Belshazzar's feast?",
      options: [
        'MENE, MENE, TEKEL, UPHARSIN',
        'ELI, ELI, LAMA SABACHTHANI',
        'TALITHA CUMI',
        'MARANATHA HOSANNA',
      ],
      correctAnswer: 'MENE, MENE, TEKEL, UPHARSIN',
      explanation:
        'Daniel 5:25 records the writing on the wall: "MENE, MENE, TEKEL, UPHARSIN"—meaning numbered, weighed in the balances, and divided to the Medes and Persians.',
    },
    tl: {
      questionText:
        'Sa Daniel 5:25-28, anong mga salita ang isinulat ng kamay sa pader ng palasyo sa piging ni Haring Belsasar?',
      options: [
        'MENE, MENE, TEKEL, UPHARSIN',
        'ELI, ELI, LAMA SABACHTHANI',
        'TALITHA CUMI',
        'MARANATHA HOSANNA',
      ],
      correctAnswer: 'MENE, MENE, TEKEL, UPHARSIN',
      explanation:
        'Sa Daniel 5:25, ang sulat sa pader ay MENE, MENE, TEKEL, UPHARSIN: binilang ng Diyos ang kaharian, tinimbang sa timbangan, at hinati sa Media at Persia.',
    },
    ceb: {
      questionText:
        'Sa Daniel 5:25-28, unsang mga pulong ang gisulat sa bungbong sa palasyo atol sa kombira ni Hari Belsasar?',
      options: [
        'MENE, MENE, TEKEL, UPHARSIN',
        'ELI, ELI, LAMA SABACHTHANI',
        'TALITHA CUMI',
        'MARANATHA HOSANNA',
      ],
      correctAnswer: 'MENE, MENE, TEKEL, UPHARSIN',
      explanation:
        'Ang Daniel 5:25 nagrekord sa sinulat nga MENE, MENE, TEKEL, UPHARSIN: giihap sa Diyos ang gingharian, gitimbang, ug gibahin sa Media ug Persia.',
    },
    ko: {
      questionText:
        '다니엘 5:25-28에서 벨사살 왕의 잔치 중 왕궁 석벽에 기록된 글자는 무엇입니까?',
      options: [
        '메네 메네 데겔 우바르신',
        '엘리 엘리 라마 사박다니',
        '달리다굼',
        '마라나타 호산나',
      ],
      correctAnswer: '메네 메네 데겔 우바르신',
      explanation:
        '다니엘 5:25은 벽에 기록된 글자가 "메네 메네 데겔 우바르신"이며, 하나님이 왕국을 세어서 끝나게 하셨고 저울에 달아 부족함이 보였으며 메대와 바사 사람에게 나뉘게 된다는 뜻임을 설명합니다.',
    },
    'zh-CN': {
      questionText: '在但以理书 5:25-28 中，伯沙撒王设摆盛筵时，王宫粉墙上所写的字是什么？',
      options: [
        '弥尼，弥尼，提客勒，乌法珥新',
        '以利，以利，拉马撒巴各大尼',
        '大利大古米',
        '主必要来，和散那',
      ],
      correctAnswer: '弥尼，弥尼，提客勒，乌法珥新',
      explanation:
        '但以理书 5:25 记载所写的文字是：“弥尼，弥尼，提客勒，乌法珥新”，意为神已经数算你国的年日、被称在天平里显出亏欠、国分裂归与玛代人和波斯人。',
    },
    'zh-TW': {
      questionText: '在但以理書 5:25-28 中，伯沙撒王設擺盛筵時，王宮粉牆上所寫的字是什麼？',
      options: [
        '彌尼，彌尼，提客勒，烏法珥新',
        '以利，以利，拉馬撒巴各大尼',
        '大利大古米',
        '主必要來，和散那',
      ],
      correctAnswer: '彌尼，彌尼，提客勒，烏法珥新',
      explanation:
        '但以理書 5:25 記載所寫的文字是：「彌尼，彌尼，提客勒，烏法珥新」，意為神已經數算你國的年日、被稱在天平裡顯出虧欠、國分裂歸與瑪代人和波斯人。',
    },
    es: {
      questionText:
        'En Daniel 5:25-28, ¿qué palabras fueron escritas en la pared del palacio durante el banquete del rey Belsasar?',
      options: [
        'MENE, MENE, TEKEL, UPARSIN',
        'ELÍ, ELÍ, ¿LAMA SABACTANI?',
        'TALITA CUMI',
        'MARANATA HOSANNA',
      ],
      correctAnswer: 'MENE, MENE, TEKEL, UPARSIN',
      explanation:
        'Daniel 5:25 registra la escritura en la pared: "MENE, MENE, TEKEL, UPARSIN"—contó Dios tu reino, pesado has sido en balanza, y roto es tu reino.',
    },
  },

  // 18. Open-Ended Reflection (Practice & Educational Mode)
  {
    questionCode: 'QB-OPEN-018',
    category: 'Psalms and Proverbs',
    bibleBook: 'Proverbs',
    testament: 'Old Testament',
    difficulty: 'Easy',
    questionFormat: 'Open-Ended Reflection',
    scriptureReference: 'Proverbs 3:5-6',
    en: {
      questionText:
        'Reflect on Proverbs 3:5-6 ("Trust in the LORD with all thine heart; and lean not unto thine own understanding. In all thy ways acknowledge him, and he shall direct thy paths"): What contrast does this passage draw between human understanding and trusting the Lord?',
      options: ['Trust in the Lord wholeheartedly rather than relying solely on human understanding'],
      correctAnswer:
        'Trusting the Lord wholeheartedly and acknowledging Him in every path, rather than relying on one\'s own limited understanding.',
      explanation:
        'Proverbs 3:5-6 contrasts relying on finite human understanding with wholehearted trust and acknowledgment of the Lord, who promises to direct and make straight one\'s paths.',
    },
    tl: {
      questionText:
        'Pagnilayan ang Kawikaan 3:5-6 ("Tumiwala ka sa Panginoon ng buong puso mo, at huwag kang manalig sa iyong sariling kaunawaan"): Ano ang ipinapakitang pagkakaiba sa pagitan ng sariling kaunawaan at buong-pusong pagtitiwala sa Panginoon?',
      options: ['Buong-pusong pagtitiwala sa Panginoon sa halip na sa sariling kaunawaan lamang'],
      correctAnswer:
        'Pagtitiwala sa Panginoon nang buong puso at pagkilala sa Kanya sa lahat ng lakad upang ituro Niya ang matuwid na landas.',
      explanation:
        'Sa Kawikaan 3:5-6, binibigyang-diin ang buong-pusong pagtitiwala sa Panginoon at pagkilala sa Kanya sa lahat ng iyong mga lakad.',
    },
    ceb: {
      questionText:
        'Pamalandungi ang Panultihon 3:5-6 ("Salig sa Ginoo sa bug-os mong kasingkasing, ug ayaw pagsalig sa imong kaugalingong salabutan"): Unsa ang kalainan tali sa kaugalingong salabutan ug sa pagsalig sa Ginoo?',
      options: ['Bug-os nga pagsalig sa Ginoo sa tanang dalan'],
      correctAnswer:
        'Ang bug-os nga pagsalig ug pag-ila sa Ginoo sa tanan nimong dalan aron Iyang tul-iron ang imong mga alagianan.',
      explanation:
        'Ang Panultihon 3:5-6 nagtudlo sa tibuok kasingkasing nga pagsalig sa Ginoo kay sa kaugalingong pagsabot.',
    },
    ko: {
      questionText:
        '잠언 3:5-6 ("너는 마음을 다하여 여호와를 신뢰하고 네 명철을 의지하지 말라 너는 범사에 그를 인정하라 그리하면 네 길을 지도하시리라")을 묵상해 보세요: 이 구절은 사람의 명철과 여호와를 신뢰하는 것 사이에 어떤 대조를 보여줍니까?',
      options: ['자신의 명철을 의지하지 않고 범사에 여호와를 인정하며 신뢰함'],
      correctAnswer:
        '자신의 한계 있는 명철을 의지하는 대신 마음을 다해 여호와를 신뢰하고 범사에 그분을 인정할 때 주께서 길을 인도하신다는 교훈입니다.',
      explanation:
        '잠언 3:5-6은 인간의 제한된 명철을 의지하는 것과 전심으로 여호와를 신뢰하고 범사에 인정하는 삶을 대조하며 하나님의 인도하심을 약속합니다.',
    },
    'zh-CN': {
      questionText:
        '默想箴言 3:5-6（“你要专心仰赖耶和华，不可倚靠自己的聪明，在你一切所行的事上都要认定他，他必指引你的路”）：这段经文在“倚靠自己的聪明”与“专心仰赖耶和华”之间作了怎样的对比？',
      options: ['专心仰赖耶和华并在凡事上认定他'],
      correctAnswer: '不倚靠人有限的聪明，而是全心信靠耶和华，在一切所行的事上认定他，蒙他指引道路。',
      explanation:
        '箴言 3:5-6 将人有限的聪明与全心仰赖、凡事认定耶和华作对比，应许神必指引道路。',
    },
    'zh-TW': {
      questionText:
        '默想箴言 3:5-6（「你要專心仰賴耶和華，不可倚靠自己的聰明，在你一切所行的事上都要認定他，他必指引你的路」）：這段經文在「倚靠自己的聰明」與「專心仰賴耶和華」之間作了怎樣的對比？',
      options: ['專心仰賴耶和華並在凡事上認定他'],
      correctAnswer: '不倚靠人有限的聰明，而是全心信靠耶和華，在一切所行的事上認定他，蒙他指引道路。',
      explanation:
        '箴言 3:5-6 將人有限的聰明與全心仰賴、凡事認定耶和華作對比，應許神必指引道路。',
    },
    es: {
      questionText:
        'Reflexione sobre Proverbios 3:5-6 ("Fíate de Jehová de todo tu corazón, y no te apoyes en tu propia prudencia"): ¿Qué contraste presenta este pasaje?',
      options: ['Confiar en Jehová de todo corazón'],
      correctAnswer:
        'Confiar en Jehová de todo corazón y reconocerlo en todos nuestros caminos en lugar de apoyarse en la prudencia humana.',
      explanation:
        'Proverbios 3:5-6 contrasta la prudencia humana con la confianza plena en Jehová, quien endereza nuestras veredas.',
    },
  },

  // 19. True or False (Genesis / Creation)
  {
    questionCode: 'QB-TF-019',
    category: 'Genesis',
    bibleBook: 'Genesis',
    testament: 'Old Testament',
    difficulty: 'Beginner',
    questionFormat: 'True or False',
    scriptureReference: 'Genesis 2:2-3',
    en: {
      questionText:
        'According to Genesis 2:2-3, God rested on the seventh day from all His work which He had made, and blessed and sanctified the seventh day.',
      options: ['TRUE', 'FALSE'],
      correctAnswer: 'TRUE',
      explanation:
        'Genesis 2:2-3 states that on the seventh day God ended His work which He had made, rested on the seventh day, and blessed and sanctified it.',
    },
    tl: {
      questionText:
        'Ayon sa Genesis 2:2-3, nagpahinga ang Diyos sa ikapitong araw mula sa lahat Niyang gawa, at binasbasan at ginawang banal ang ikapitong araw.',
      options: ['TAMA (TRUE)', 'MALI (FALSE)'],
      correctAnswer: 'TAMA (TRUE)',
      explanation:
        'Nakasaad sa Genesis 2:2-3 na nagpahinga ang Diyos sa ikapitong araw at Kanyang binasbasan at ipinangilin ito.',
    },
    ceb: {
      questionText:
        'Sumala sa Genesis 2:2-3, mipahulay ang Diyos sa ikapitong adlaw gikan sa tanan Niyang buhat, ug gipanalanginan ug gibalaan ang ikapitong adlaw.',
      options: ['TINUOD (TRUE)', 'BAKAK (FALSE)'],
      correctAnswer: 'TINUOD (TRUE)',
      explanation:
        'Ang Genesis 2:2-3 nagpamatuod nga mipahulay ang Diyos sa ikapitong adlaw ug gipanalanginan kini.',
    },
    ko: {
      questionText:
        '창세기 2:2-3에 따르면, 하나님께서는 일곱째 날에 그가 하시던 모든 일을 마치시고 안식하시며 그 날을 복되게 하사 거룩하게 하셨습니다.',
      options: ['참 (TRUE)', '거짓 (FALSE)'],
      correctAnswer: '참 (TRUE)',
      explanation:
        '창세기 2:2-3은 하나님이 일곱째 날에 안식하시고 그 날을 복되게 하사 거룩하게 하셨음을 기록합니다.',
    },
    'zh-CN': {
      questionText:
        '根据创世记 2:2-3 记载，神在第七日歇了他一切的工，赐福给第七日，定为圣日。',
      options: ['正确 (TRUE)', '错误 (FALSE)'],
      correctAnswer: '正确 (TRUE)',
      explanation:
        '创世记 2:2-3 记载神在第七日歇了他一切创造的工，赐福给第七日，定为圣日。',
    },
    'zh-TW': {
      questionText:
        '根據創世記 2:2-3 記載，神在第七日歇了他一切的工，賜福給第七日，定為聖日。',
      options: ['正確 (TRUE)', '錯誤 (FALSE)'],
      correctAnswer: '正確 (TRUE)',
      explanation:
        '創世記 2:2-3 記載神在第七日歇了他一切創造的工，賜福給第七日，定為聖日。',
    },
    es: {
      questionText:
        'Según Génesis 2:2-3, Dios reposó el séptimo día de toda la obra que hizo, y bendijo y santificó el séptimo día.',
      options: ['VERDADERO', 'FALSO'],
      correctAnswer: 'VERDADERO',
      explanation:
        'Génesis 2:2-3 declara que Dios reposó el día séptimo de toda su obra y lo bendijo y santificó.',
    },
  },

  // 20. True or False (Acts / Paul's Conversion)
  {
    questionCode: 'QB-TF-020',
    category: 'Acts',
    bibleBook: 'Acts',
    testament: 'New Testament',
    difficulty: 'Intermediate',
    questionFormat: 'True or False',
    scriptureReference: 'Acts 9:3-4',
    en: {
      questionText:
        'According to Acts 9:3-4, Saul of Tarsus was traveling on the road to Alexandria in Egypt when a light from heaven shone round about him.',
      options: ['TRUE', 'FALSE'],
      correctAnswer: 'FALSE',
      explanation:
        'False. According to Acts 9:2-3, Saul was journeying toward Damascus (not Alexandria) when the light from heaven shone around him.',
    },
    tl: {
      questionText:
        'Ayon sa Mga Gawa 9:3-4, si Saulo ng Tarso ay naglalakbay patungong Alejandria sa Ehipto nang magliwanag sa palibot niya ang isang ilaw mula sa langit.',
      options: ['TAMA (TRUE)', 'MALI (FALSE)'],
      correctAnswer: 'MALI (FALSE)',
      explanation:
        'Mali. Ayon sa Mga Gawa 9:2-3, si Saulo ay naglalakbay patungong Damasco nang magpakita sa kanya ang Panginoon.',
    },
    ceb: {
      questionText:
        'Sumala sa Buhat 9:3-4, si Saulo sa Tarso nagpanaw paingon sa Alejandria sa Ehipto sa dihang misidlak ang kahayag gikan sa langit.',
      options: ['TINUOD (TRUE)', 'BAKAK (FALSE)'],
      correctAnswer: 'BAKAK (FALSE)',
      explanation:
        'Bakak. Sumala sa Buhat 9:2-3, si Saulo nagpanaw paingon sa Damasco.',
    },
    ko: {
      questionText:
        '사도행전 9:3-4에 따르면, 다소 사람 사울은 애굽의 알렉산드리아로 가는 길에서 하늘로부터 비치는 빛을 보았습니다.',
      options: ['참 (TRUE)', '거짓 (FALSE)'],
      correctAnswer: '거짓 (FALSE)',
      explanation:
        '거짓입니다. 사도행전 9:2-3에 따르면 사울은 다메섹(다마스쿠스)으로 가던 길에 하늘로부터 빛이 둘러 비추었습니다.',
    },
    'zh-CN': {
      questionText:
        '根据使徒行传 9:3-4 记载，大数的扫罗是在前往埃及亚历山大的路上被天上发光四面照着。',
      options: ['正确 (TRUE)', '错误 (FALSE)'],
      correctAnswer: '错误 (FALSE)',
      explanation:
        '错误。使徒行传 9:2-3 记载扫罗是在往大马士革（大马色）去的路上被天上的光照着。',
    },
    'zh-TW': {
      questionText:
        '根據使徒行傳 9:3-4 記載，大數的掃羅是在前往埃及亞歷山大的路上被天上發光四面照著。',
      options: ['正確 (TRUE)', '錯誤 (FALSE)'],
      correctAnswer: '錯誤 (FALSE)',
      explanation:
        '錯誤。使徒行傳 9:2-3 記載掃羅是在往大馬士革（大馬色）去的路上被天上的光照著。',
    },
    es: {
      questionText:
        'Según Hechos 9:3-4, Saulo de Tarso viajaba hacia Alejandría en Egipto cuando le rodeó un resplandor de luz del cielo.',
      options: ['VERDADERO', 'FALSO'],
      correctAnswer: 'FALSO',
      explanation:
        'Falso. Según Hechos 9:2-3, Saulo iba camino de Damasco cuando le rodeó la luz del cielo.',
    },
  },

  // 21. Fill in the Blank (John 3:16)
  {
    questionCode: 'QB-GOS-021',
    category: 'The Four Gospels',
    bibleBook: 'John',
    testament: 'New Testament',
    difficulty: 'Beginner',
    questionFormat: 'Fill in the Blank',
    scriptureReference: 'John 3:16',
    en: {
      questionText:
        '"For God so loved the ______, that he gave his only begotten Son, that whosoever believeth in him should not perish, but have everlasting life."',
      options: ['world', 'temple', 'angels', 'prophets'],
      correctAnswer: 'world',
      acceptedAnswers: ['world', 'the world'],
      explanation:
        'John 3:16 declares: "For God so loved the world, that he gave his only begotten Son, that whosoever believeth in him should not perish, but have everlasting life."',
    },
    tl: {
      questionText:
        '"Sapagka\'t gayon na lamang ang pagsinta ng Diyos sa ______, na ibinigay niya ang kaniyang bugtong na Anak." (Juan 3:16)',
      options: ['sanlibutan', 'templo', 'mga anghel', 'mga propeta'],
      correctAnswer: 'sanlibutan',
      acceptedAnswers: ['sanlibutan', 'mundo', 'world'],
      explanation:
        'Sa Juan 3:16, gayon na lamang ang pag-ibig ng Diyos sa sanlibutan kaya ibinigay Niya ang Kanyang bugtong na Anak.',
    },
    ceb: {
      questionText:
        '"Kay gihigugma gayod sa Diyos ang ______, nga tungod niana gihatag niya ang iyang bugtong Anak." (Juan 3:16)',
      options: ['kalibotan', 'templo', 'mga manulonda', 'mga manalagna'],
      correctAnswer: 'kalibotan',
      acceptedAnswers: ['kalibotan', 'kalibutan', 'world'],
      explanation:
        'Ang Juan 3:16 nagpahayag sa dakong gugma sa Diyos alang sa kalibotan.',
    },
    ko: {
      questionText:
        '"하나님이 ______을(를) 이처럼 사랑하사 독생자를 주셨으니 이는 그를 믿는 자마다 멸망하지 않고 영생을 얻게 하려 하심이라." (요한복음 3:16)',
      options: ['세상', '성전', '천사들', '선지자들'],
      correctAnswer: '세상',
      acceptedAnswers: ['세상', 'world'],
      explanation:
        '요한복음 3:16은 "하나님이 세상을 이처럼 사랑하사 독생자를 주셨으니"라고 선포합니다.',
    },
    'zh-CN': {
      questionText:
        '“神爱______，甚至将他的独生子赐给他们，叫一切信他的，不至灭亡，反得永生。”（约翰福音 3:16）',
      options: ['世人', '圣殿', '众天使', '众先知'],
      correctAnswer: '世人',
      acceptedAnswers: ['世人', '世界', 'world'],
      explanation: '约翰福音 3:16 宣告：“神爱世人，甚至将他的独生子赐给他们。”',
    },
    'zh-TW': {
      questionText:
        '「神愛______，甚至將他的獨生子賜給他們，叫一切信他的，不至滅亡，反得永生。」（約翰福音 3:16）',
      options: ['世人', '聖殿', '眾天使', '眾先知'],
      correctAnswer: '世人',
      acceptedAnswers: ['世人', '世界', 'world'],
      explanation: '約翰福音 3:16 宣告：「神愛世人，甚至將他的獨生子賜給他們。」',
    },
    es: {
      questionText:
        '"Porque de tal manera amó Dios al ______, que ha dado a su Hijo unigénito." (Juan 3:16)',
      options: ['mundo', 'templo', 'cielo', 'profeta'],
      correctAnswer: 'mundo',
      acceptedAnswers: ['mundo', 'el mundo', 'world'],
      explanation:
        'Juan 3:16 declara: "Porque de tal manera amó Dios al mundo, que ha dado a su Hijo unigénito."',
    },
  },

  // 22. Matching (Four Gospels & Key Openings)
  {
    questionCode: 'QB-MAT-022',
    category: 'The Four Gospels',
    bibleBook: 'Luke',
    testament: 'New Testament',
    difficulty: 'Advanced',
    questionFormat: 'Matching',
    scriptureReference: 'Matthew 1:1; Mark 1:1; Luke 1:3; John 1:1',
    en: {
      questionText:
        'Match each of the Four Gospels with its distinctive opening description in Scripture:',
      options: [
        'The book of the generation of Jesus Christ, the son of David',
        'The beginning of the gospel of Jesus Christ, the Son of God',
        'Addressed in order unto most excellent Theophilus',
        'In the beginning was the Word, and the Word was with God',
      ],
      correctAnswer:
        'Matthew -> Generation of Jesus | Mark -> Beginning of the gospel | Luke -> Most excellent Theophilus | John -> In the beginning was the Word',
      matchingPairs: [
        {
          left: 'Gospel of Matthew',
          right: 'The book of the generation of Jesus Christ, the son of David',
        },
        {
          left: 'Gospel of Mark',
          right: 'The beginning of the gospel of Jesus Christ, the Son of God',
        },
        {
          left: 'Gospel of Luke',
          right: 'Addressed in order unto most excellent Theophilus',
        },
        {
          left: 'Gospel of John',
          right: 'In the beginning was the Word, and the Word was with God',
        },
      ],
      explanation:
        'Matthew 1:1 opens with the genealogy of Jesus; Mark 1:1 opens with the beginning of the gospel; Luke 1:3 addresses most excellent Theophilus; and John 1:1 begins with "In the beginning was the Word."',
    },
    tl: {
      questionText:
        'Itugma ang bawat isa sa Apat na Ebanghelyo sa natatanging pambungad na paglalarawan nito sa Kasulatan:',
      options: [
        'Ang aklat ng lahi ni Jesucristo, na anak ni David',
        'Ang pasimula ng evangelio ni Jesucristo, na Anak ng Diyos',
        'Isinulat nang sunud-sunod para sa kagalang-galang na Teofilo',
        'Nang pasimula ay ang Salita, at ang Salita ay sumasa Diyos',
      ],
      correctAnswer:
        'Mateo -> Lahi ni Jesucristo | Marcos -> Pasimula ng evangelio | Lucas -> Kagalang-galang na Teofilo | Juan -> Nang pasimula ay ang Salita',
      matchingPairs: [
        {
          left: 'Mateo',
          right: 'Ang aklat ng lahi ni Jesucristo, na anak ni David',
        },
        {
          left: 'Marcos',
          right: 'Ang pasimula ng evangelio ni Jesucristo, na Anak ng Diyos',
        },
        {
          left: 'Lucas',
          right: 'Isinulat nang sunud-sunod para sa kagalang-galang na Teofilo',
        },
        {
          left: 'Juan',
          right: 'Nang pasimula ay ang Salita, at ang Salita ay sumasa Diyos',
        },
      ],
      explanation:
        'Ang Mateo 1:1, Marcos 1:1, Lucas 1:3, at Juan 1:1 ay may kani-kaniyang natatanging pambungad sa Bagong Tipan.',
    },
    ceb: {
      questionText:
        'Ipares ang matag usa sa Upat ka Ebanghelyo ngadto sa iyang sinugdanan nga paghulagway:',
      options: [
        'Ang basahon sa kaliwatan ni Jesukristo, anak ni David',
        'Ang sinugdanan sa maayong balita ni Jesukristo',
        'Gisulat alang kang halangdong Teofilo',
        'Sa sinugdan mao ang Pulong, ug ang Pulong uban sa Diyos',
      ],
      correctAnswer:
        'Mateo -> Kaliwatan | Marcos -> Sinugdanan sa maayong balita | Lucas -> Teofilo | Juan -> Sa sinugdan mao ang Pulong',
      matchingPairs: [
        {
          left: 'Mateo',
          right: 'Ang basahon sa kaliwatan ni Jesukristo, anak ni David',
        },
        {
          left: 'Marcos',
          right: 'Ang sinugdanan sa maayong balita ni Jesukristo',
        },
        {
          left: 'Lucas',
          right: 'Gisulat alang kang halangdong Teofilo',
        },
        {
          left: 'Juan',
          right: 'Sa sinugdan mao ang Pulong, ug ang Pulong uban sa Diyos',
        },
      ],
      explanation:
        'Ang Mateo 1:1, Marcos 1:1, Lucas 1:3, ug Juan 1:1 nagpakita sa talagsaong sinugdanan sa matag Ebanghelyo.',
    },
    ko: {
      questionText:
        '사복음서의 각 책과 성경에 기록된 시작 구절의 특징을 바르게 짝지으세요:',
      options: [
        '아브라함과 다윗의 자손 예수 그리스도의 계보라',
        '하나님의 아들 예수 그리스도의 복음의 시작이라',
        '데오빌로 각하에게 차례대로 써 보내는 기록',
        '태초에 말씀이 계시니라 이 말씀이 하나님과 함께 계셨으니',
      ],
      correctAnswer:
        '마태복음 -> 계보 | 마가복음 -> 복음의 시작 | 누가복음 -> 데오빌로 | 요한복음 -> 태초에 말씀이 계시니라',
      matchingPairs: [
        {
          left: '마태복음',
          right: '아브라함과 다윗의 자손 예수 그리스도의 계보라',
        },
        {
          left: '마가복음',
          right: '하나님의 아들 예수 그리스도의 복음의 시작이라',
        },
        {
          left: '누가복음',
          right: '데오빌로 각하에게 차례대로 써 보내는 기록',
        },
        {
          left: '요한복음',
          right: '태초에 말씀이 계시니라 이 말씀이 하나님과 함께 계셨으니',
        },
      ],
      explanation:
        '마태복음 1:1, 마가복음 1:1, 누가복음 1:3, 요한복음 1:1은 각각 고유한 서두로 시작합니다.',
    },
    'zh-CN': {
      questionText: '请将四福音书与它们在圣经中独特的开篇描述配对：',
      options: [
        '亚伯拉罕的后裔，大卫的子孙，耶稣基督的家谱',
        '神的儿子，耶稣基督福音的起头',
        '按着次序写给提阿非罗大人',
        '太初有道，道与神同在，道就是神',
      ],
      correctAnswer:
        '马太福音 -> 家谱 | 马可福音 -> 福音的起头 | 路加福音 -> 提阿非罗 | 约翰福音 -> 太初有道',
      matchingPairs: [
        {
          left: '马太福音',
          right: '亚伯拉罕的后裔，大卫的子孙，耶稣基督的家谱',
        },
        {
          left: '马可福音',
          right: '神的儿子，耶稣基督福音的起头',
        },
        {
          left: '路加福音',
          right: '按着次序写给提阿非罗大人',
        },
        {
          left: '约翰福音',
          right: '太初有道，道与神同在，道就是神',
        },
      ],
      explanation:
        '马太福音 1:1、马可福音 1:1、路加福音 1:3 与约翰福音 1:1 各自有独特的开篇记载。',
    },
    'zh-TW': {
      questionText: '請將四福音書與它們在聖經中獨特的開篇描述配對：',
      options: [
        '亞伯拉罕的後裔，大衛的子孫，耶穌基督的家譜',
        '神的兒子，耶穌基督福音的起頭',
        '按著次序寫給提阿非羅大人',
        '太初有道，道與神同在，道就是神',
      ],
      correctAnswer:
        '馬太福音 -> 家譜 | 馬可福音 -> 福音的起頭 | 路加福音 -> 提阿非羅 | 約翰福音 -> 太初有道',
      matchingPairs: [
        {
          left: '馬太福音',
          right: '亞伯拉罕的後裔，大衛的子孫，耶穌基督的家譜',
        },
        {
          left: '馬可福音',
          right: '神的兒子，耶穌基督福音的起頭',
        },
        {
          left: '路加福音',
          right: '按著次序寫給提阿非羅大人',
        },
        {
          left: '約翰福音',
          right: '太初有道，道與神同在，道就是神',
        },
      ],
      explanation:
        '馬太福音 1:1、馬可福音 1:1、路加福音 1:3 與約翰福音 1:1 各自有獨特的開篇記載。',
    },
    es: {
      questionText:
        'Empareje cada uno de los cuatro Evangelios con su descripción inicial en las Escrituras:',
      options: [
        'Libro de la genealogía de Jesucristo, hijo de David',
        'Principio del evangelio de Jesucristo, Hijo de Dios',
        'Escrito por orden al excelentísimo Teófilo',
        'En el principio era el Verbo, y el Verbo era con Dios',
      ],
      correctAnswer:
        'Mateo -> Genealogía | Marcos -> Principio del evangelio | Lucas -> Excelentísimo Teófilo | Juan -> En el principio era el Verbo',
      matchingPairs: [
        {
          left: 'Mateo',
          right: 'Libro de la genealogía de Jesucristo, hijo de David',
        },
        {
          left: 'Marcos',
          right: 'Principio del evangelio de Jesucristo, Hijo de Dios',
        },
        {
          left: 'Lucas',
          right: 'Escrito por orden al excelentísimo Teófilo',
        },
        {
          left: 'Juan',
          right: 'En el principio era el Verbo, y el Verbo era con Dios',
        },
      ],
      explanation:
        'Mateo 1:1, Marcos 1:1, Lucas 1:3 y Juan 1:1 presentan introducciones únicas en el Nuevo Testamento.',
    },
  },

  // 23. Multiple Choice (Historical Books / Kings and Queens / Solomon)
  {
    questionCode: 'QB-KNG-023',
    category: 'Kings and Queens',
    bibleBook: '1 Kings',
    testament: 'Old Testament',
    difficulty: 'Easy',
    questionFormat: 'Multiple Choice',
    scriptureReference: '1 Kings 3:9',
    en: {
      questionText:
        'When God appeared to young King Solomon at Gibeon and asked what He should give him, what did Solomon ask for?',
      options: [
        'An understanding heart to judge God\'s people and discern between good and bad',
        'Ten thousand chariots of iron and victory over Egypt',
        'Long life and the wealth of the kings of Tyre',
        'A wall of bronze around Jerusalem',
      ],
      correctAnswer:
        'An understanding heart to judge God\'s people and discern between good and bad',
      explanation:
        'In 1 Kings 3:9, Solomon asked God: "Give therefore thy servant an understanding heart to judge thy people, that I may discern between good and bad."',
    },
    tl: {
      questionText:
        'Nang magpakita ang Diyos sa batang Haring Solomon sa Gabaon at tanungin kung ano ang nais niyang hingin, ano ang hiniling ni Solomon?',
      options: [
        'Isang maunawaing puso upang humatol sa bayan ng Diyos at makakilala ng mabuti at masama',
        'Sampung libong karwaheng bakal',
        'Mahabang buhay at kayamanan ng Ehipto',
        'Pader na tanso sa palibot ng Jerusalem',
      ],
      correctAnswer:
        'Isang maunawaing puso upang humatol sa bayan ng Diyos at makakilala ng mabuti at masama',
      explanation:
        'Sa 1 Mga Hari 3:9, hiniling ni Solomon ang isang maunawaing puso upang makahatol nang matuwid sa bayan ng Diyos.',
    },
    ceb: {
      questionText:
        'Sa dihang nagpakita ang Diyos kang Hari Solomon sa Gabaon, unsa ang gipangayo ni Solomon?',
      options: [
        'Usa ka masinabuton nga kasingkasing aron sa paghukom sa katawhan ug pag-ila sa maayo ug dautan',
        'Napulo ka libo ka karwahe nga puthaw',
        'Taas nga kinabuhi ug bahandi sa Ehipto',
        'Paril nga tumbaga sa Jerusalem',
      ],
      correctAnswer:
        'Usa ka masinabuton nga kasingkasing aron sa paghukom sa katawhan ug pag-ila sa maayo ug dautan',
      explanation:
        'Sa 1 Mga Hari 3:9, gipangayo ni Solomon ang masinabuton nga kasingkasing aron sa paghukom sa katawhan sa Diyos.',
    },
    ko: {
      questionText:
        '하나님께서 기브온에서 젊은 솔로몬 왕에게 나타나 무엇을 줄꼬 물으셨을 때, 솔로몬은 무엇을 구하였습니까?',
      options: [
        '주의 백성을 재판하여 선악을 분별할 수 있는 지혜로운 마음 (듣는 마음)',
        '철 병거 만 대와 애굽에 대한 승리',
        '장수와 두로 왕들의 부귀',
        '예루살렘을 둘러싼 놋 성벽',
      ],
      correctAnswer:
        '주의 백성을 재판하여 선악을 분별할 수 있는 지혜로운 마음 (듣는 마음)',
      explanation:
        '열왕기상 3:9에서 솔로몬은 "누가 주의 이 많은 백성을 재판할 수 있사오리이까 듣는 마음을 종에게 주사 주의 백성을 재판하여 선악을 분별하게 하옵소서"라고 구했습니다.',
    },
    'zh-CN': {
      questionText:
        '当神在基遍向年轻的所罗门王显现，问他要什么时，所罗门求的是什么？',
      options: [
        '赐我智慧可以判断你的民，能辨别是非',
        '一万辆铁车和战胜埃及的能力',
        '长寿与推罗诸王的财富',
        '围绕耶路撒冷的铜墙',
      ],
      correctAnswer: '赐我智慧可以判断你的民，能辨别是非',
      explanation:
        '列王纪上 3:9 记载所罗门求神：“求你赐我智慧，可以判断你的民，能辨别是非。”',
    },
    'zh-TW': {
      questionText:
        '當神在基遍向年輕的所羅門王顯現，問他要什麼時，所羅門求的是什麼？',
      options: [
        '賜我智慧可以判斷你的民，能辨別是非',
        '一萬輛鐵車和戰勝埃及的能力',
        '長壽與推羅諸王的財富',
        '圍繞耶路撒冷的銅牆',
      ],
      correctAnswer: '賜我智慧可以判斷你的民，能辨別是非',
      explanation:
        '列王紀上 3:9 記載所羅門求神：「求你賜我智慧，可以判斷你的民，能辨別是非。」',
    },
    es: {
      questionText:
        'Cuando Dios se apareció al joven rey Salomón en Gabaón y le preguntó qué quería que le diera, ¿qué pidió Salomón?',
      options: [
        'Un corazón entendido para juzgar al pueblo de Dios y discernir entre lo bueno y lo malo',
        'Diez mil carros de hierro y victoria sobre Egipto',
        'Larga vida y las riquezas de Tiro',
        'Un muro de bronce alrededor de Jerusalén',
      ],
      correctAnswer:
        'Un corazón entendido para juzgar al pueblo de Dios y discernir entre lo bueno y lo malo',
      explanation:
        'En 1 Reyes 3:9, Salomón pidió: "Da, pues, a tu siervo corazón entendido para juzgar a tu pueblo, y para discernir entre lo bueno y lo malo."',
    },
  },

  // 24. Multiple Choice (Parables / Teachings of Jesus)
  {
    questionCode: 'QB-PAR-024',
    category: 'Parables',
    bibleBook: 'Luke',
    testament: 'New Testament',
    difficulty: 'Easy',
    questionFormat: 'Multiple Choice',
    scriptureReference: 'Luke 10:33-34',
    en: {
      questionText:
        'In Jesus\' parable in Luke 10:30-37, after a priest and a Levite passed by a wounded traveler on the road from Jerusalem to Jericho, who stopped and showed compassion on him?',
      options: [
        'A certain Samaritan',
        'A Roman centurion',
        'A scribe of Capernaum',
        'A merchant of Damascus',
      ],
      correctAnswer: 'A certain Samaritan',
      explanation:
        'Luke 10:33-34 records that a certain Samaritan had compassion on the wounded man, bound up his wounds pouring in oil and wine, and brought him to an inn.',
    },
    tl: {
      questionText:
        'Sa talinghaga ni Hesus sa Lucas 10:30-37, matapos dumaan ang isang saserdote at isang Levita, sino ang huminto at nagpakita ng habag sa sugatang manlalakbay?',
      options: [
        'Isang Samaritano',
        'Isang senturyong Romano',
        'Isang eskriba ng Capernaum',
        'Isang mangangalakal ng Damasco',
      ],
      correctAnswer: 'Isang Samaritano',
      explanation:
        'Ayon sa Lucas 10:33-34, isang Samaritano ang nahabag sa kanya, tinalian ang kanyang mga sugat, at dinala siya sa bahay-panuluyan.',
    },
    ceb: {
      questionText:
        'Sa sambingay ni Hesus sa Lucas 10:30-37, kinsa ang mihunong ug naluoy sa samaran nga tawo sa dalan gikan sa Jerusalem paingon sa Jerico?',
      options: [
        'Usa ka Samarianhon',
        'Usa ka Romanhong senturyon',
        'Usa ka eskriba sa Capernaum',
        'Usa ka magpapatigayon sa Damasco',
      ],
      correctAnswer: 'Usa ka Samarianhon',
      explanation:
        'Ang Lucas 10:33-34 nag-ingon nga usa ka Samarianhon ang naluoy kaniya ug nagtambal sa iyang mga samad.',
    },
    ko: {
      questionText:
        '누가복음 10:30-37의 비유에서 예루살렘에서 여리고로 내려가다가 강도 만난 자를 제사장과 레위인이 지나친 후, 누가 그를 불쌍히 여겨 돌보았습니까?',
      options: [
        '어떤 사마리아 사람',
        '로마 백부장',
        '가버나움의 서기관',
        '다메섹의 상인',
      ],
      correctAnswer: '어떤 사마리아 사람',
      explanation:
        '누가복음 10:33-34은 어떤 사마리아 사람이 그를 보고 불쌍히 여겨 기름과 포도주를 상처에 붓고 싸매어 주막으로 데리고 갔음을 기록합니다.',
    },
    'zh-CN': {
      questionText:
        '在路加福音 10:30-37 耶稣的比喻中，祭司和利未人从受伤的人旁边过去后，是谁动了慈心照料他？',
      options: [
        '一个撒玛利亚人',
        '一位罗马百夫长',
        '迦百农的文士',
        '大马士革的商人',
      ],
      correctAnswer: '一个撒玛利亚人',
      explanation:
        '路加福音 10:33-34 记载有一个撒玛利亚人行路来到那里，看见他就动了慈心，包裹他的伤处，带他到客店里照应他。',
    },
    'zh-TW': {
      questionText:
        '在路加福音 10:30-37 耶穌的比喻中，祭司和利未人從受傷的人旁邊過去後，是誰動了慈心照料他？',
      options: [
        '一個撒瑪利亞人',
        '一位羅馬百夫長',
        '迦百農的文士',
        '大馬士革的商人',
      ],
      correctAnswer: '一個撒瑪利亞人',
      explanation:
        '路加福音 10:33-34 記載有一個撒瑪利亞人行路來到那裡，看見他就動了慈心，包裹他的傷處，帶他到客店裡照應他。',
    },
    es: {
      questionText:
        'En la parábola de Jesús en Lucas 10:30-37, ¿quién tuvo compasión del hombre herido en el camino de Jerusalén a Jericó?',
      options: [
        'Un samaritano',
        'Un centurión romano',
        'Un escriba de Capernaum',
        'Un mercader de Damasco',
      ],
      correctAnswer: 'Un samaritano',
      explanation:
        'Lucas 10:33-34 registra que un samaritano fue movido a misericordia, vendó sus heridas y cuidó de él.',
    },
  },
];

export const SEED_ACHIEVEMENTS = [
  {
    code: 'FIRST_VICTORY',
    title: 'First Victory',
    description: 'Complete your first Bible quiz session.',
    icon: 'Trophy',
    xpReward: 20,
    category: 'milestone',
  },
  {
    code: 'PERFECT_SCORE',
    title: 'Perfect Score',
    description: 'Achieve 100% accuracy in a quiz session.',
    icon: 'Award',
    xpReward: 40,
    category: 'mastery',
  },
  {
    code: 'BIBLE_SCHOLAR',
    title: 'Bible Scholar',
    description: 'Answer at least 25 canonical Bible questions.',
    icon: 'BookOpen',
    xpReward: 35,
    category: 'mastery',
  },
  {
    code: 'SCRIPTURE_MASTER',
    title: 'Scripture Master',
    description: 'Answer 100 Bible questions across Old and New Testaments.',
    icon: 'Crown',
    xpReward: 85,
    category: 'mastery',
  },
  {
    code: 'SEVEN_DAY_STREAK',
    title: 'Seven-Day Streak',
    description: 'Maintain a 7-day Daily Bible Challenge streak.',
    icon: 'Flame',
    xpReward: 50,
    category: 'dedication',
  },
  {
    code: 'TEN_STREAK_MASTER',
    title: 'Ten Consecutive Correct',
    description: 'Answer 10 consecutive questions right without a mistake.',
    icon: 'Zap',
    xpReward: 45,
    category: 'skill',
  },
  {
    code: 'MULTIPLAYER_CHAMPION',
    title: 'Multiplayer Champion',
    description: 'Win an online multiplayer Scripture match.',
    icon: 'Swords',
    xpReward: 40,
    category: 'multiplayer',
  },
  {
    code: 'OLD_TESTAMENT_EXPERT',
    title: 'Old Testament Expert',
    description: 'Answer 15 Old Testament questions accurately.',
    icon: 'Scroll',
    xpReward: 35,
    category: 'testament',
  },
  {
    code: 'NEW_TESTAMENT_EXPERT',
    title: 'New Testament Expert',
    description: 'Answer 15 New Testament questions accurately.',
    icon: 'Compass',
    xpReward: 35,
    category: 'testament',
  },
  {
    code: 'BIBLE_MARATHON',
    title: 'Bible Marathon',
    description: 'Complete 10 full Bible quiz sessions.',
    icon: 'Medal',
    xpReward: 65,
    category: 'dedication',
  },
];
